import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import jsPDF from 'jspdf';
import {
  Activity,
  AlertTriangle,
  Award,
  BarChart3,
  BookOpen,
  Brain,
  CheckCircle2,
  Database,
  Eye,
  FileArchive,
  FileText,
  GitBranch,
  GraduationCap,
  Loader2,
  MessageSquare,
  Play,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';
import { toast } from '../hooks/use-toast';

type Lookback = '1h' | '4h' | '8h' | '12h' | '24h' | '7d' | '30d' | '60d' | '90d';
type SimulationMode = 'fast' | 'deep' | 'training';
type QueueSortKey = 'incident' | 'severity' | 'status' | 'classification' | 'alerts' | 'created' | 'lastModified';
type SortDirection = 'asc' | 'desc';
type Subtab = 'queue' | 'runs' | 'replay' | 'ml' | 'learning' | 'quality' | 'clusters' | 'tuning' | 'response' | 'case_evidence' | 'exports' | 'settings';
type StepStatus = 'pending' | 'running' | 'completed' | 'warning' | 'failed' | 'skipped';

type SimulationIncident = {
  incidentNumber: string;
  incidentId: string;
  title: string;
  severity: string;
  status: string;
  createdTime: string | null;
  lastModifiedTime: string | null;
  owner: string;
  classification: string;
  providerName: string;
  providerIncidentId: string;
  alertsCount: number;
};

type SimulationSummary = {
  verdict?: string;
  confidence?: number;
  reasoningQuality?: string;
  reasons?: string[];
  hypotheses?: string[];
  readOnlyGuarantees?: string[];
  safeKql?: { generated?: number; completed?: number; warnings?: number; blocked?: number; rowsReturned?: number; guardrail?: string };
  counts?: { alerts?: number; entities?: number; entityGroups?: number; tactics?: number; safeKqlRows?: number };
  mlHandoff?: { eligible?: boolean; signalSource?: string; insightType?: string; label?: string; weight?: number };
};

type EntityGraphNode = { id?: string; label?: string; type?: string; tone?: string; value?: string };
type EntityGraphEdge = { from?: string; to?: string; label?: string; type?: string };
type EntityGraph = { nodes?: EntityGraphNode[]; edges?: EntityGraphEdge[] };

type SimulationRunSummary = {
  id: string;
  customerId: string;
  incidentId: string;
  incidentNumber: string;
  incidentTitle: string;
  severity: string;
  status: string;
  mode: SimulationMode;
  readOnly: boolean;
  simulationStatus: string;
  actualClassification: string | null;
  actualDetermination: string | null;
  finalVerdict: string | null;
  confidence: number;
  reasoningQuality: string | null;
  mlFeedbackStatus: string | null;
  mlFeedbackEventId: string | null;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number;
  createdByEmail: string | null;
  summary?: SimulationSummary;
  entityGraph?: EntityGraph;
  recommendations?: string[];
};

type SimulationStep = {
  id: string;
  order: number;
  type: string;
  title: string;
  status: StepStatus;
  durationMs: number;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  evidence: unknown[];
  confidenceImpact: number;
  createdAt: string | null;
};

type SimulationQuery = {
  id: string;
  stepId: string;
  purpose: string;
  kql: string;
  tables: string[];
  rowsReturned: number;
  executionTimeMs: number;
  resultSummary: string;
  status: StepStatus;
  error: string | null;
  guardrailStatus?: string;
  guardrailReasons?: string[];
  resultPreview?: Array<Record<string, unknown>>;
  createdAt: string | null;
};

type SimulationEvidence = {
  id: string;
  stepId: string;
  type: string;
  title: string;
  severity: string | null;
  entityType: string | null;
  entityValue: string | null;
  source: string | null;
  details: Record<string, unknown>;
  confidenceImpact: number;
  createdAt: string | null;
};

type SimulationRunDetail = SimulationRunSummary & {
  steps: SimulationStep[];
  queries: SimulationQuery[];
  evidence: SimulationEvidence[];
};


type SimulationComparison = {
  runId: string;
  generatedAt: string;
  actualVsAi?: { actualClassification?: string; actualFamily?: string; aiVerdict?: string; aiFamily?: string; status?: string; score?: number; explanation?: string };
  mlInsights?: {
    scoreAvailable?: boolean;
    mlPriorityScore?: number | null;
    falsePositiveLikelihood?: number | null;
    confidence?: number | null;
    recommendedAction?: string | null;
    explanation?: unknown[];
    incidentScores?: unknown[];
    feedbackSignals?: Array<Record<string, unknown>>;
  };
  similarIncidents?: {
    summary?: { similarRunCount?: number; likelyNoisePatternCount?: number; likelyTpPatternCount?: number; averageSimilarity?: number };
    runs?: Array<Record<string, unknown>>;
  };
  analystFeedback?: Array<Record<string, unknown>>;
};

type SimulationExportPayload = {
  generatedAt: string;
  run: SimulationRunDetail;
  comparison: SimulationComparison | null;
  executivePack: {
    usedAi?: boolean;
    model?: string;
    executiveSummary?: string;
    analystTrainingSummary?: string;
    customerSafeReport?: string;
    keyFindings?: string[];
    recommendedActions?: string[];
    limitations?: string[];
    aiWarning?: string;
  };
  trainingPack?: { filename?: string; markdown?: string };
  customerSafeReport?: { filename?: string; markdown?: string };
};

type LearningCheckpoint = {
  checkpointId: string;
  stepOrder: number;
  stepType: string;
  title: string;
  status: string;
  confidenceImpact?: number;
  outputPreview?: Record<string, unknown>;
  whyItMatters: string;
  question: string;
  expectedAnswer: string;
  expectedKeywords?: string[];
  skillTags: string[];
  analystAnswer?: string;
  score?: number;
  isCorrect?: boolean;
  feedback?: string;
  answeredAt?: string | null;
};

type LearningPlan = {
  runId: string;
  generatedAt: string;
  mode?: string;
  incident?: Record<string, unknown>;
  summary?: { totalCheckpoints?: number; answeredCheckpoints?: number; finalScore?: number; finalLevel?: string; completion?: Record<string, unknown> | null };
  skillTags?: string[];
  modules?: LearningCheckpoint[];
};

type QualityMetric = { id: string; label: string; score: number; level: string; helper: string };
type QualityDashboard = {
  runId: string;
  generatedAt: string;
  overallScore: number;
  overallLevel: string;
  metrics: QualityMetric[];
  confidenceTimeline: Array<{ stepOrder?: string | number; stepType?: string; title?: string; status?: string; impact?: number; confidence?: number; explanation?: string }>;
  recommendedAction?: string;
  riskWarnings?: string[];
  scoringNotes?: string[];
};

type DeepClusterCandidate = {
  id?: string;
  incidentNumber?: string;
  incidentTitle?: string;
  severity?: string;
  status?: string;
  actualClassification?: string;
  finalVerdict?: string;
  confidence?: number;
  similarityScore?: number;
  similarityReasons?: string[];
  dimensions?: Record<string, number>;
  startedAt?: string | null;
};

type DeepClusters = {
  runId: string;
  generatedAt: string;
  incident?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  clusters?: Array<{ id?: string; label?: string; helper?: string; value?: string; count?: number }>;
  candidates?: DeepClusterCandidate[];
  recurringFalsePositivePatterns?: Array<Record<string, unknown>>;
  tuningCandidates?: Array<Record<string, unknown>>;
};

type TuningRecommendation = {
  runId: string;
  generatedAt: string;
  incident?: Record<string, unknown>;
  detectionQuality?: Record<string, unknown>;
  socDebt?: Record<string, unknown>;
  recommendations?: Array<Record<string, unknown>>;
  suppressionOptions?: string[];
  safeKqlTuningDraft?: string;
  clusterContext?: DeepClusters;
};


type ResponseRecommendationPreview = {
  runId: string;
  generatedAt: string;
  readOnlyGuarantees?: string[];
  incident?: Record<string, unknown>;
  responseActions?: Array<{ category?: string; riskLevel?: string; actions?: Array<Record<string, unknown>> }>;
  responseOpsTemplates?: Array<Record<string, unknown>>;
  customerUpdateDraft?: string;
  internalAnalystNotes?: string;
  qualitySummary?: Record<string, unknown> | null;
  tuningSummary?: Record<string, unknown> | null;
};

type CaseEvidenceLink = {
  id?: string;
  attachmentId?: string | null;
  exportPackId?: string | null;
  artifactType?: string;
  filename?: string;
  customerVisible?: boolean;
  createdAt?: string | null;
};

type CaseEvidenceAttachResult = {
  runId?: string;
  attachments?: Array<Record<string, unknown>>;
  exportPack?: Record<string, unknown> | null;
  links?: CaseEvidenceLink[];
  responsePreviewAvailable?: boolean;
};

type Props = {
  customerId?: string;
  customerName?: string;
  onNavigate?: (tabId: string, customerId?: string) => void;
};

const LOOKBACKS: Lookback[] = ['24h', '7d', '30d', '60d', '90d'];
const SUBTABS: Array<{ id: Subtab; label: string; helper: string }> = [
  { id: 'queue', label: 'Incident Queue', helper: 'Load real Sentinel incidents and start a read-only simulation.' },
  { id: 'runs', label: 'Simulation Runs', helper: 'Review recent simulations and open any replay.' },
  { id: 'replay', label: 'Investigation Replay', helper: 'Flight-recorder view of steps, queries, evidence and verdict.' },
  { id: 'ml', label: 'ML + Verdict Compare', helper: 'Compare actual, AI and ML outcomes, then capture feedback.' },
  { id: 'learning', label: 'Learning Mode', helper: 'Guided SOC training with checkpoints and skill scoring.' },
  { id: 'quality', label: 'Quality Score', helper: 'Score evidence, query, entity, ML and verdict agreement quality.' },
  { id: 'clusters', label: 'Deep Clustering', helper: 'Find recurring FP patterns across title, entities, owner and verdicts.' },
  { id: 'tuning', label: 'Detection Tuning', helper: 'Recommend rule tuning, exclusions, watchlists and SOC Debt findings.' },
  { id: 'response', label: 'Response Preview', helper: 'Read-only response actions, customer update and analyst notes.' },
  { id: 'case_evidence', label: 'Case Evidence Pack', helper: 'Attach reports and replay snapshots to Case Evidence.' },
  { id: 'exports', label: 'Exports & Training Pack', helper: 'Export JSON, PDF, executive summary and customer-safe report.' },
  { id: 'settings', label: 'Lab Settings', helper: 'Guardrails, permissions and safe operating modes.' },
];

const QUEUE_FETCH_LIMIT = 500;
const SEVERITY_RANK: Record<string, number> = { critical: 5, high: 4, medium: 3, low: 2, informational: 1, info: 1, unknown: 0 };

function compareValues(a: unknown, b: unknown): number {
  const left = a instanceof Date ? a.getTime() : typeof a === 'number' ? a : String(a ?? '').toLowerCase();
  const right = b instanceof Date ? b.getTime() : typeof b === 'number' ? b : String(b ?? '').toLowerCase();
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right));
}

function sortLabel(label: string, key: QueueSortKey, sortKey: QueueSortKey, sortDir: SortDirection): ReactNode {
  return <span className="inline-flex items-center gap-1">{label}<span className="text-[10px] text-slate-500">{sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span></span>;
}

function text(value: unknown, max = 180): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value);
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, Math.max(0, max - 1))}…` : clean;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return 'n/a';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function pct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return 'n/a';
  const normalized = value <= 1 ? value * 100 : value;
  return `${Math.round(normalized)}%`;
}

function severityClass(severity: string | null | undefined): string {
  const s = (severity ?? '').toLowerCase();
  if (s === 'critical' || s === 'high') return 'bg-red-500/15 text-red-100 border-red-500/40';
  if (s === 'medium') return 'bg-amber-500/15 text-amber-100 border-amber-500/40';
  if (s === 'low') return 'bg-sky-500/15 text-sky-100 border-sky-500/40';
  return 'bg-slate-500/15 text-slate-200 border-slate-500/40';
}

function verdictClass(verdict: string | null | undefined): string {
  const v = (verdict ?? '').toLowerCase();
  if (v.includes('true') || v.includes('malicious')) return 'bg-red-500/15 text-red-100 border-red-500/40';
  if (v.includes('false') || v.includes('benign')) return 'bg-emerald-500/15 text-emerald-100 border-emerald-500/40';
  if (v.includes('tuning') || v.includes('review')) return 'bg-amber-500/15 text-amber-100 border-amber-500/40';
  return 'bg-slate-500/15 text-slate-200 border-slate-500/40';
}

function statusClass(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s.includes('failed')) return 'bg-red-500/15 text-red-100 border-red-500/40';
  if (s.includes('warning')) return 'bg-amber-500/15 text-amber-100 border-amber-500/40';
  if (s.includes('completed')) return 'bg-emerald-500/15 text-emerald-100 border-emerald-500/40';
  if (s.includes('running')) return 'bg-cyan-500/15 text-cyan-100 border-cyan-500/40';
  return 'bg-slate-500/15 text-slate-200 border-slate-500/40';
}

function badge(label: string, cls: string) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>;
}


function downloadTextFile(filename: string, content: string, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadSimulationPdf(payload: SimulationExportPayload, filename: string) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = 14;
  const margin = 14;
  const add = (value: string, size = 9, bold = false) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(value || ' ', 182);
    for (const line of lines) {
      if (y > 280) { pdf.addPage(); y = 14; }
      pdf.text(String(line), margin, y);
      y += size >= 14 ? 7 : 5;
    }
  };
  const run = payload.run;
  const pack = payload.executivePack ?? {};
  add('KloudSecure AI Simulation Lab Report', 16, true);
  add(`Incident #${run.incidentNumber || 'n/a'} - ${run.incidentTitle || 'Untitled incident'}`, 11, true);
  add(`Generated: ${formatDate(payload.generatedAt)} · Read-only simulation · No Sentinel changes performed`, 8);
  y += 2;
  add('Executive Summary', 12, true);
  add(pack.executiveSummary || 'No executive summary available.', 9);
  y += 2;
  add('Verdict Comparison', 12, true);
  add(`Actual Sentinel classification: ${run.actualClassification ?? 'Unknown'}`, 9);
  add(`AI simulated verdict: ${run.finalVerdict ?? 'n/a'} · Confidence: ${pct(run.confidence)} · Reasoning quality: ${run.reasoningQuality ?? 'n/a'}`, 9);
  const actualVsAi = payload.comparison?.actualVsAi;
  if (actualVsAi) add(`Comparison: ${actualVsAi.status ?? 'n/a'} - ${actualVsAi.explanation ?? ''}`, 9);
  y += 2;
  add('ML Insights', 12, true);
  const ml = payload.comparison?.mlInsights;
  add(ml?.scoreAvailable ? `ML priority score ${pct(ml.mlPriorityScore ?? 0)} · false-positive likelihood ${pct(ml.falsePositiveLikelihood ?? 0)} · confidence ${pct(ml.confidence ?? 0)}` : 'No cached ML incident score was available for this run.', 9);
  if (ml?.recommendedAction) add(`Recommended action: ${ml.recommendedAction}`, 9);
  y += 2;
  add('Analyst Training Summary', 12, true);
  add(pack.analystTrainingSummary || 'No analyst training summary available.', 9);
  y += 2;
  add('Customer-safe Report', 12, true);
  add(pack.customerSafeReport || 'No customer-safe report available.', 9);
  pdf.save(filename.replace(/\.json$/i, '.pdf'));
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-2xl border border-slate-700/80 bg-slate-950/55 p-8 text-center text-slate-300">
      <ShieldCheck className="mx-auto mb-3 h-9 w-9 text-cyan-300" />
      <div className="text-lg font-semibold text-white">{title}</div>
      <div className="mt-2 text-sm text-slate-400">{description}</div>
    </div>
  );
}

export function IncidentOpsAiSimulationLabTab({ customerId, customerName, onNavigate }: Props) {
  const { canCapability } = useAuth();
  const { customers, setActiveCustomerId } = useCustomers();
  const [activeSubtab, setActiveSubtab] = useState<Subtab>('queue');
  const [lookback, setLookback] = useState<Lookback>('7d');
  const [queueSortKey, setQueueSortKey] = useState<QueueSortKey>('lastModified');
  const [queueSortDir, setQueueSortDir] = useState<SortDirection>('desc');
  const [incidents, setIncidents] = useState<SimulationIncident[]>([]);
  const [runs, setRuns] = useState<SimulationRunSummary[]>([]);
  const [selectedRun, setSelectedRun] = useState<SimulationRunDetail | null>(null);
  const [queueDiagnostics, setQueueDiagnostics] = useState<string[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runningIncident, setRunningIncident] = useState<string | null>(null);
  const [loadingReplayId, setLoadingReplayId] = useState<string | null>(null);
  const [sendingMl, setSendingMl] = useState(false);
  const [comparison, setComparison] = useState<SimulationComparison | null>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState('agree_ai');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [exportPack, setExportPack] = useState<SimulationExportPayload | null>(null);
  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [learningPlan, setLearningPlan] = useState<LearningPlan | null>(null);
  const [loadingLearning, setLoadingLearning] = useState(false);
  const [checkpointAnswers, setCheckpointAnswers] = useState<Record<string, string>>({});
  const [answeringCheckpoint, setAnsweringCheckpoint] = useState<string | null>(null);
  const [completingLearning, setCompletingLearning] = useState(false);
  const [qualityDashboard, setQualityDashboard] = useState<QualityDashboard | null>(null);
  const [loadingQuality, setLoadingQuality] = useState(false);
  const [deepClusters, setDeepClusters] = useState<DeepClusters | null>(null);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [tuningRecommendation, setTuningRecommendation] = useState<TuningRecommendation | null>(null);
  const [loadingTuning, setLoadingTuning] = useState(false);
  const [sendingTuningFinding, setSendingTuningFinding] = useState(false);
  const [responsePreview, setResponsePreview] = useState<ResponseRecommendationPreview | null>(null);
  const [loadingResponsePreview, setLoadingResponsePreview] = useState(false);
  const [caseEvidenceLinks, setCaseEvidenceLinks] = useState<CaseEvidenceLink[]>([]);
  const [caseEvidenceResult, setCaseEvidenceResult] = useState<CaseEvidenceAttachResult | null>(null);
  const [loadingCaseEvidence, setLoadingCaseEvidence] = useState(false);
  const [attachingCaseEvidence, setAttachingCaseEvidence] = useState(false);
  const [caseEvidenceCustomerVisible, setCaseEvidenceCustomerVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRead = canCapability('simulation_lab', 'read');
  const canRun = canCapability('simulation_lab', 'run');
  const canReplay = canCapability('simulation_lab', 'replay');
  const canSendToMl = canCapability('simulation_lab', 'send_to_ml');
  const canViewEvidence = canCapability('simulation_lab', 'view_evidence');
  const canCompareVerdict = canCapability('simulation_lab', 'compare_verdict');
  const canExport = canCapability('simulation_lab', 'export');

  const selectedSummary = selectedRun?.summary ?? {};
  const entityGraph = selectedRun?.entityGraph ?? {};
  const graphNodes = Array.isArray(entityGraph.nodes) ? entityGraph.nodes : [];
  const graphEdges = Array.isArray(entityGraph.edges) ? entityGraph.edges : [];
  const incidentCountLabel = useMemo(() => `${incidents.length} incident${incidents.length === 1 ? '' : 's'}`, [incidents.length]);
  const customerOptions = useMemo(() => customers.filter((customer) => customer.id !== 'default' && customer.enabled !== false), [customers]);

  function setQueueSort(key: QueueSortKey) {
    setQueueSortKey((prev) => {
      if (prev === key) {
        setQueueSortDir((dir) => dir === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setQueueSortDir(key === 'incident' || key === 'alerts' || key === 'created' || key === 'lastModified' ? 'desc' : 'asc');
      return key;
    });
  }

  const sortedIncidents = useMemo(() => {
    const rows = [...incidents];
    rows.sort((a, b) => {
      let result = 0;
      if (queueSortKey === 'incident') result = compareValues(Number(a.incidentNumber) || a.incidentNumber, Number(b.incidentNumber) || b.incidentNumber);
      else if (queueSortKey === 'severity') result = compareValues(SEVERITY_RANK[(a.severity || 'unknown').toLowerCase()] ?? 0, SEVERITY_RANK[(b.severity || 'unknown').toLowerCase()] ?? 0);
      else if (queueSortKey === 'status') result = compareValues(a.status, b.status);
      else if (queueSortKey === 'classification') result = compareValues(a.classification, b.classification);
      else if (queueSortKey === 'alerts') result = compareValues(a.alertsCount, b.alertsCount);
      else if (queueSortKey === 'created') result = compareValues(new Date(a.createdTime ?? 0), new Date(b.createdTime ?? 0));
      else result = compareValues(new Date(a.lastModifiedTime ?? a.createdTime ?? 0), new Date(b.lastModifiedTime ?? b.createdTime ?? 0));
      return queueSortDir === 'asc' ? result : -result;
    });
    return rows;
  }, [incidents, queueSortDir, queueSortKey]);

  const queueColumns = useMemo<FilterableColumnDef<SimulationIncident>[]>(() => [
    {
      key: 'incident',
      header: 'Incident',
      headerText: 'Incident',
      value: (incident) => `#${incident.incidentNumber || 'n/a'} ${incident.title} ${incident.providerName} ${incident.owner}`,
      sortLabel: sortLabel('Incident', 'incident', queueSortKey, queueSortDir),
      onSort: () => setQueueSort('incident'),
      render: (incident) => (
        <div className="min-w-[360px]">
          <div className="font-semibold text-white">#{incident.incidentNumber || 'n/a'} · {text(incident.title, 96)}</div>
          <div className="mt-1 text-xs text-slate-400">{incident.providerName} · Owner: {incident.owner || 'Unassigned'}</div>
        </div>
      ),
    },
    { key: 'severity', header: 'Severity', headerText: 'Severity', value: (incident) => incident.severity || 'Unknown', sortLabel: sortLabel('Severity', 'severity', queueSortKey, queueSortDir), onSort: () => setQueueSort('severity'), render: (incident) => badge(incident.severity || 'Unknown', severityClass(incident.severity)) },
    { key: 'status', header: 'Status', headerText: 'Status', value: (incident) => incident.status || 'Unknown', sortLabel: sortLabel('Status', 'status', queueSortKey, queueSortDir), onSort: () => setQueueSort('status'), render: (incident) => badge(incident.status || 'Unknown', statusClass(incident.status)) },
    { key: 'classification', header: 'Actual classification', headerText: 'Actual classification', value: (incident) => incident.classification || 'Unknown', sortLabel: sortLabel('Actual classification', 'classification', queueSortKey, queueSortDir), onSort: () => setQueueSort('classification'), render: (incident) => <span className="text-slate-300">{incident.classification || 'Unknown'}</span> },
    { key: 'alerts', header: 'Alerts', headerText: 'Alerts', value: (incident) => incident.alertsCount, sortLabel: sortLabel('Alerts', 'alerts', queueSortKey, queueSortDir), onSort: () => setQueueSort('alerts'), render: (incident) => <span className="font-semibold text-slate-200">{incident.alertsCount}</span> },
    { key: 'created', header: 'Created', headerText: 'Created', value: (incident) => formatDate(incident.createdTime), sortLabel: sortLabel('Created', 'created', queueSortKey, queueSortDir), onSort: () => setQueueSort('created'), render: (incident) => <span className="text-slate-300">{formatDate(incident.createdTime)}</span> },
    { key: 'lastModified', header: 'Last modified', headerText: 'Last modified', value: (incident) => formatDate(incident.lastModifiedTime), sortLabel: sortLabel('Last modified', 'lastModified', queueSortKey, queueSortDir), onSort: () => setQueueSort('lastModified'), render: (incident) => <span className="text-slate-300">{formatDate(incident.lastModifiedTime)}</span> },
    {
      key: 'simulation',
      header: 'Simulation',
      headerText: 'Simulation',
      value: () => 'fast deep training',
      menuEnabled: false,
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      render: (incident) => {
        const runningBase = incident.incidentNumber || incident.incidentId;
        return (
          <div className="flex justify-end gap-2">
            {(['fast', 'deep', 'training'] as SimulationMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  void runSimulation(incident, mode);
                }}
                disabled={!customerId || !canRun || runningIncident !== null}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-100 hover:border-cyan-400 hover:bg-cyan-500/10 disabled:cursor-not-allowed disabled:opacity-50"
                title={!customerId ? 'Select a customer first' : !canRun ? 'Requires simulation_lab.run' : `Run ${mode} simulation`}
              >
                {runningIncident === `${runningBase}:${mode}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                {mode}
              </button>
            ))}
          </div>
        );
      },
    },
  ], [canRun, customerId, queueSortDir, queueSortKey, runningIncident]);

  const loadQueue = useCallback(async (forceRefresh = false) => {
    if (!customerId || !canRead) return;
    setLoadingQueue(true);
    setError(null);
    try {
      const data = await apiFetch<{ ok: true; readOnly: boolean; incidents: SimulationIncident[]; diagnostics?: string[]; cached?: boolean; total?: number; cache?: { loadedAt?: string; expiresAt?: string } }>(`/simulation-lab/incidents?customerId=${encodeURIComponent(customerId)}&lookback=${encodeURIComponent(lookback)}&limit=${QUEUE_FETCH_LIMIT}${forceRefresh ? '&refresh=true' : ''}`);
      setIncidents(data.incidents ?? []);
      setQueueDiagnostics(data.diagnostics ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'Simulation Lab incident load failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingQueue(false);
    }
  }, [canRead, customerId, lookback]);

  const loadRuns = useCallback(async () => {
    if (!canRead) return;
    setLoadingRuns(true);
    setError(null);
    try {
      const query = customerId ? `?customerId=${encodeURIComponent(customerId)}&limit=50` : '?limit=50';
      const data = await apiFetch<{ ok: true; runs: SimulationRunSummary[] }>(`/simulation-lab/runs${query}`);
      setRuns(data.runs ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'Simulation runs load failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingRuns(false);
    }
  }, [canRead, customerId]);

  const loadReplay = useCallback(async (runId: string) => {
    if (!canReplay) return;
    setLoadingReplayId(runId);
    setError(null);
    try {
      const data = await apiFetch<{ ok: true; run: SimulationRunDetail }>(`/simulation-lab/runs/${encodeURIComponent(runId)}`);
      setSelectedRun(data.run);
      setComparison(null);
      setExportPack(null);
      setLearningPlan(null);
      setQualityDashboard(null);
      setDeepClusters(null);
      setTuningRecommendation(null);
      setCheckpointAnswers({});
      setActiveSubtab('replay');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'Simulation replay failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingReplayId(null);
    }
  }, [canReplay]);


  const loadComparison = useCallback(async (runId: string) => {
    if (!canCompareVerdict) return;
    setLoadingComparison(true);
    try {
      const data = await apiFetch<{ ok: true; comparison: SimulationComparison }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/comparison`);
      setComparison(data.comparison);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Verdict comparison failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingComparison(false);
    }
  }, [canCompareVerdict]);

  const loadLearning = useCallback(async (runId: string) => {
    if (!canReplay) return;
    setLoadingLearning(true);
    try {
      const data = await apiFetch<{ ok: true; learning: LearningPlan }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/learning`);
      setLearningPlan(data.learning);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Learning mode load failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingLearning(false);
    }
  }, [canReplay]);

  const loadQuality = useCallback(async (runId: string) => {
    if (!canCompareVerdict) return;
    setLoadingQuality(true);
    try {
      const data = await apiFetch<{ ok: true; quality: QualityDashboard }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/quality`);
      setQualityDashboard(data.quality);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Simulation quality score failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingQuality(false);
    }
  }, [canCompareVerdict]);

  const loadDeepClusters = useCallback(async (runId: string) => {
    if (!canCompareVerdict) return;
    setLoadingClusters(true);
    try {
      const data = await apiFetch<{ ok: true; clusters: DeepClusters }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/deep-clusters`);
      setDeepClusters(data.clusters);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Deep clustering failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingClusters(false);
    }
  }, [canCompareVerdict]);

  const loadTuningRecommendation = useCallback(async (runId: string) => {
    if (!canCompareVerdict) return;
    setLoadingTuning(true);
    try {
      const data = await apiFetch<{ ok: true; tuning: TuningRecommendation }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/tuning`);
      setTuningRecommendation(data.tuning);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Detection tuning recommendation failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingTuning(false);
    }
  }, [canCompareVerdict]);

  async function sendTuningFinding() {
    if (!selectedRun || !canCompareVerdict) return;
    setSendingTuningFinding(true);
    try {
      const data = await apiFetch<{ ok: true; finding: Record<string, unknown> }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/tuning/send`, { method: 'POST' });
      if (data.finding?.tuning) setTuningRecommendation(data.finding.tuning as TuningRecommendation);
      toast({ title: 'Tuning finding stored', description: `Finding ${String(data.finding?.findingId ?? 'created')} sent to Detection Quality / SOC Debt queue.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Tuning finding failed', description: message, variant: 'destructive' });
    } finally {
      setSendingTuningFinding(false);
    }
  }


  const loadResponsePreview = useCallback(async (runId: string) => {
    if (!canReplay) return;
    setLoadingResponsePreview(true);
    try {
      const data = await apiFetch<{ ok: true; preview: ResponseRecommendationPreview }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/response-preview`);
      setResponsePreview(data.preview);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Response preview failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingResponsePreview(false);
    }
  }, [canReplay]);

  const loadCaseEvidenceLinks = useCallback(async (runId: string) => {
    if (!canExport) return;
    setLoadingCaseEvidence(true);
    try {
      const data = await apiFetch<{ ok: true; links: CaseEvidenceLink[] }>(`/simulation-lab/runs/${encodeURIComponent(runId)}/case-evidence`);
      setCaseEvidenceLinks(data.links ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Case Evidence status failed', description: message, variant: 'destructive' });
    } finally {
      setLoadingCaseEvidence(false);
    }
  }, [canExport]);

  async function attachCaseEvidencePack() {
    if (!selectedRun || !canExport) return;
    setAttachingCaseEvidence(true);
    try {
      const data = await apiFetch<{ ok: true; result: CaseEvidenceAttachResult }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/case-evidence/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerVisible: caseEvidenceCustomerVisible, includeExecutiveSummary: true, includeCustomerSafeReport: true, includeTrainingPack: true, includeReplaySnapshot: true, includeResponseRecommendations: true, createExportPack: true }),
      });
      setCaseEvidenceResult(data.result);
      setCaseEvidenceLinks(data.result.links ?? []);
      toast({ title: 'Case Evidence pack created', description: `${data.result.attachments?.length ?? 0} artifact(s) attached${data.result.exportPack ? ` and export pack ${String(data.result.exportPack.id ?? '')} created` : ''}.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Case Evidence attach failed', description: message, variant: 'destructive' });
    } finally {
      setAttachingCaseEvidence(false);
    }
  }

  async function submitCheckpointAnswer(checkpointId: string) {
    if (!selectedRun || !canReplay) return;
    const analystAnswer = (checkpointAnswers[checkpointId] || '').trim();
    if (!analystAnswer) {
      toast({ title: 'Answer required', description: 'Add your analyst answer before submitting the checkpoint.', variant: 'destructive' });
      return;
    }
    setAnsweringCheckpoint(checkpointId);
    try {
      const data = await apiFetch<{ ok: true; answer: Record<string, unknown>; learning: LearningPlan }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/learning/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checkpointId, analystAnswer }),
      });
      setLearningPlan(data.learning);
      setCheckpointAnswers((prev) => ({ ...prev, [checkpointId]: '' }));
      toast({ title: 'Checkpoint answered', description: `Score ${pct(Number(data.answer.score ?? 0))} · ${String(data.answer.feedback ?? '')}` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Checkpoint submission failed', description: message, variant: 'destructive' });
    } finally {
      setAnsweringCheckpoint(null);
    }
  }

  async function completeLearningMode() {
    if (!selectedRun || !canReplay) return;
    setCompletingLearning(true);
    try {
      const data = await apiFetch<{ ok: true; completion: Record<string, unknown>; learning: LearningPlan }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/learning/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Completed from IncidentOps AI Simulation Lab Learning Mode.' }),
      });
      setLearningPlan(data.learning);
      toast({ title: 'Training completed', description: `Final analyst score ${pct(Number(data.completion.finalScore ?? 0))} · ${String(data.completion.finalLevel ?? '')}` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Training completion failed', description: message, variant: 'destructive' });
    } finally {
      setCompletingLearning(false);
    }
  }

  async function submitFeedback() {
    if (!selectedRun || !canCompareVerdict) return;
    setSendingMl(true);
    try {
      const data = await apiFetch<{ ok: true; feedback: Record<string, unknown>; comparison: SimulationComparison }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: feedbackValue, notes: feedbackNotes, sendToMl: true }),
      });
      setComparison(data.comparison);
      setFeedbackNotes('');
      toast({ title: 'Simulation feedback captured', description: `Stored analyst feedback ${String(data.feedback.feedback ?? feedbackValue)} and linked it to ML training signals.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Feedback capture failed', description: message, variant: 'destructive' });
    } finally {
      setSendingMl(false);
    }
  }

  async function exportRun(format: 'json' | 'pdf_payload' | 'training_pack' | 'customer_safe_report', regenerateAi = false) {
    if (!selectedRun || !canExport) return;
    setExportingFormat(format);
    try {
      const data = await apiFetch<{ ok: true; format: string; filename: string; payload: SimulationExportPayload }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, regenerateAi }),
      });
      setExportPack(data.payload);
      if (format === 'json') {
        downloadTextFile(data.filename, JSON.stringify(data.payload, null, 2), 'application/json;charset=utf-8');
      } else if (format === 'training_pack') {
        downloadTextFile(data.payload.trainingPack?.filename || data.filename, data.payload.trainingPack?.markdown || JSON.stringify(data.payload, null, 2), 'text/markdown;charset=utf-8');
      } else if (format === 'customer_safe_report') {
        downloadTextFile(data.payload.customerSafeReport?.filename || data.filename, data.payload.customerSafeReport?.markdown || data.payload.executivePack?.customerSafeReport || '', 'text/markdown;charset=utf-8');
      } else {
        downloadSimulationPdf(data.payload, data.filename.replace(/\.json$/i, '.pdf'));
      }
      toast({ title: 'Simulation export generated', description: `${format.replace(/_/g, ' ')} exported for incident #${selectedRun.incidentNumber || 'n/a'}.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Simulation export failed', description: message, variant: 'destructive' });
    } finally {
      setExportingFormat(null);
    }
  }

  async function runSimulation(incident: SimulationIncident, mode: SimulationMode) {
    if (!customerId) {
      toast({ title: 'Select a customer first', description: 'Simulation Lab runs are customer-scoped and require an active customer context.', variant: 'destructive' });
      return;
    }
    if (!canRun) {
      toast({ title: 'Simulation permission required', description: 'Your role needs simulation_lab.run to start Fast, Deep or Training simulations.', variant: 'destructive' });
      return;
    }
    if (!incident.incidentNumber && !incident.incidentId) {
      toast({ title: 'Simulation cannot start', description: 'The selected queue row does not include an incident number or incident id.', variant: 'destructive' });
      return;
    }
    setRunningIncident(`${incident.incidentNumber || incident.incidentId}:${mode}`);
    setError(null);
    try {
      const data = await apiFetch<{ ok: true; run: SimulationRunDetail }>(`/simulation-lab/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, incidentNumber: incident.incidentNumber, incidentId: incident.incidentId, mode, autoSendToMl: false }),
      });
      setSelectedRun(data.run);
      setComparison(null);
      setExportPack(null);
      setLearningPlan(null);
      setQualityDashboard(null);
      setCheckpointAnswers({});
      toast({ title: 'Simulation completed', description: `${incident.title} → ${data.run.finalVerdict ?? 'verdict pending'} (${pct(data.run.confidence)})` });
      setActiveSubtab('replay');
      void loadRuns();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'Simulation run failed', description: message, variant: 'destructive' });
    } finally {
      setRunningIncident(null);
    }
  }

  async function sendToMl() {
    if (!selectedRun || !canSendToMl) return;
    setSendingMl(true);
    setError(null);
    try {
      const data = await apiFetch<{ ok: true; inserted: boolean; eventId: string | null; status: string; label: string; weight: number }>(`/simulation-lab/runs/${encodeURIComponent(selectedRun.id)}/ml-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: 'Sent from IncidentOps AI Simulation Lab ML Training Handoff.' }),
      });
      toast({ title: 'Simulation label sent to ML Insights', description: `${data.label} · status ${data.status} · weight ${data.weight}` });
      await loadReplay(selectedRun.id);
      void loadRuns();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({ title: 'ML handoff failed', description: message, variant: 'destructive' });
    } finally {
      setSendingMl(false);
    }
  }

  useEffect(() => {
    if (selectedRun?.id && canCompareVerdict) {
      void loadComparison(selectedRun.id);
    } else {
      setComparison(null);
    }
  }, [selectedRun?.id, canCompareVerdict, loadComparison]);

  useEffect(() => {
    if (selectedRun?.id && activeSubtab === 'learning') {
      void loadLearning(selectedRun.id);
    }
  }, [selectedRun?.id, activeSubtab, loadLearning]);

  useEffect(() => {
    if (selectedRun?.id && activeSubtab === 'quality') {
      void loadQuality(selectedRun.id);
    }
  }, [selectedRun?.id, activeSubtab, loadQuality]);

  useEffect(() => {
    if (selectedRun?.id && activeSubtab === 'clusters') {
      void loadDeepClusters(selectedRun.id);
    }
  }, [selectedRun?.id, activeSubtab, loadDeepClusters]);

  useEffect(() => {
    if (selectedRun?.id && activeSubtab === 'tuning') {
      void loadTuningRecommendation(selectedRun.id);
    }
  }, [selectedRun?.id, activeSubtab, loadTuningRecommendation]);


  useEffect(() => {
    if (selectedRun?.id && activeSubtab === 'response') {
      void loadResponsePreview(selectedRun.id);
    }
  }, [selectedRun?.id, activeSubtab, loadResponsePreview]);

  useEffect(() => {
    if (selectedRun?.id && activeSubtab === 'case_evidence') {
      void loadCaseEvidenceLinks(selectedRun.id);
    }
  }, [selectedRun?.id, activeSubtab, loadCaseEvidenceLinks]);

  useEffect(() => {
    if (customerId && canRead) {
      void loadQueue();
      void loadRuns();
    } else {
      setIncidents([]);
      setRuns([]);
      setSelectedRun(null);
    }
  }, [customerId, canRead, loadQueue, loadRuns]);

  if (!customerId) {
    return (
      <div className="space-y-4">
        <HeaderBlock customerName={customerName} customerId={customerId} customerOptions={customerOptions} onCustomerChange={(id) => { setActiveCustomerId(id); onNavigate?.('simulation_lab', id); }} />
        <EmptyState title="Select a customer to start Simulation Lab" description="AI Simulation Lab is customer-scoped because it reads Sentinel incidents, alert timelines and ML training context through customer credentials." />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="space-y-4">
        <HeaderBlock customerName={customerName} customerId={customerId} customerOptions={customerOptions} onCustomerChange={(id) => { setActiveCustomerId(id); onNavigate?.('simulation_lab', id); }} />
        <EmptyState title="Permission required" description="Your role needs simulation_lab.read in Masters → Permissions Master before this lab can load incident data." />
      </div>
    );
  }

  return (
    <div className="space-y-5 rounded-3xl bg-slate-950 p-5 text-slate-100">
      <HeaderBlock customerName={customerName} customerId={customerId} customerOptions={customerOptions} onCustomerChange={(id) => { setActiveCustomerId(id); onNavigate?.('simulation_lab', id); }} />

      <div className="grid gap-3 md:grid-cols-4">
        <MetricCard icon={<Database className="h-4 w-4" />} label="Incident queue" value={incidentCountLabel} helper={`${lookback} lookback · read-only KQL`} />
        <MetricCard icon={<Activity className="h-4 w-4" />} label="Simulation runs" value={runs.length} helper="Stored replay runs" />
        <MetricCard icon={<Brain className="h-4 w-4" />} label="Selected verdict" value={selectedRun?.finalVerdict ? selectedRun.finalVerdict.replace(/_/g, ' ') : 'n/a'} helper={selectedRun ? `${pct(selectedRun.confidence)} confidence` : 'Open a replay'} />
        <MetricCard icon={<GraduationCap className="h-4 w-4" />} label="ML handoff" value={selectedRun?.mlFeedbackStatus ?? 'not sent'} helper="Weak pseudo-label for training" />
      </div>

      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-4 shadow-lg shadow-cyan-950/20">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 text-cyan-200" />
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-100">READ-ONLY SIMULATION</div>
              <div className="mt-1 text-sm text-cyan-50/90">The lab reads incidents, alerts, entities and ML context only. It does not close incidents, assign owners, add tags/comments, trigger playbooks, create bookmarks or modify watchlists.</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {badge('No Sentinel writes', 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
            {badge('Audit enabled', 'border-indigo-400/40 bg-indigo-400/10 text-indigo-100')}
            {badge('ML weak labels', 'border-violet-400/40 bg-violet-400/10 text-violet-100')}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-2">
        <div className="grid gap-2 md:grid-cols-4 xl:grid-cols-12">
          {SUBTABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubtab(tab.id)}
              className={`rounded-xl px-4 py-3 text-left transition ${activeSubtab === tab.id ? 'bg-cyan-500/15 text-cyan-50 ring-1 ring-cyan-400/40' : 'text-slate-300 hover:bg-slate-800/70 hover:text-white'}`}
            >
              <div className="text-sm font-semibold">{tab.label}</div>
              <div className="mt-1 text-xs text-slate-400">{tab.helper}</div>
            </button>
          ))}
        </div>
      </div>

      {activeSubtab === 'queue' && (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-lg font-semibold text-white">Incident Queue</div>
              <div className="text-sm text-slate-400">Load recent Sentinel incidents for {customerName ?? customerId}, then run Fast, Deep or Training simulations.</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={lookback} onChange={(e) => setLookback(e.target.value as Lookback)} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100">
                {LOOKBACKS.map((lb) => <option key={lb} value={lb}>{lb}</option>)}
              </select>
              <button onClick={() => void loadQueue(true)} disabled={loadingQueue} className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-60">
                {loadingQueue ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh Queue
              </button>
            </div>
          </div>
          {queueDiagnostics.length > 0 && <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-3 text-xs text-slate-300">{queueDiagnostics.join(' ')}</div>}
          {incidents.length === 0 && !loadingQueue ? (
            <EmptyState title="No incidents loaded" description="Refresh the queue after selecting a lookback. The backend reads SecurityIncident only and never writes back to Sentinel." />
          ) : (
            <div className="rounded-2xl border border-slate-700/80 bg-slate-950/55 p-3">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                <span>Cached queue rows: <strong className="text-slate-200">{incidents.length.toLocaleString()}</strong> · sorted by <strong className="text-slate-200">{queueSortKey}</strong> {queueSortDir}</span>
                <span>Use each column’s <strong>⋯</strong> menu for checklist filters, text filters and autosize. Pagination is applied after filters.</span>
              </div>
              <FilterableColumnTable<SimulationIncident>
                columns={queueColumns}
                rows={sortedIncidents}
                storageKey={`simulation-lab-incident-queue-${customerId}-${lookback}`}
                rowKey={(incident) => `${incident.incidentNumber}-${incident.incidentId}`}
                emptyMessage="No incidents match the current column filters."
                maxHeightClassName="max-h-[720px]"
                tableClassName="min-w-full text-sm"
                paginationEnabled
                defaultPageSize={50}
                pageSizeOptions={[25, 50, 100]}
              />
            </div>
          )}
        </section>
      )}

      {activeSubtab === 'runs' && (
        <section className="space-y-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-lg font-semibold text-white">Simulation Runs</div>
              <div className="text-sm text-slate-400">All runs are persisted for replay, verdict comparison and ML handoff.</div>
            </div>
            <button onClick={() => void loadRuns()} disabled={loadingRuns} className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-white disabled:opacity-60">
              {loadingRuns ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh Runs
            </button>
          </div>
          {runs.length === 0 && !loadingRuns ? (
            <EmptyState title="No simulation runs yet" description="Run a simulation from Incident Queue. The completed run will appear here with verdict, confidence and ML handoff status." />
          ) : (
            <div className="grid gap-3">
              {runs.map((run) => (
                <div key={run.id} className="rounded-2xl border border-slate-700/80 bg-slate-950/55 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-cyan-100">#{run.incidentNumber || 'n/a'}</span>
                        {badge(run.severity || 'Unknown', severityClass(run.severity))}
                        {badge(run.simulationStatus || 'unknown', statusClass(run.simulationStatus))}
                        {badge(run.finalVerdict ? run.finalVerdict.replace(/_/g, ' ') : 'verdict pending', verdictClass(run.finalVerdict))}
                      </div>
                      <div className="mt-2 text-base font-semibold text-white">{text(run.incidentTitle, 140)}</div>
                      <div className="mt-1 text-xs text-slate-400">{run.mode} mode · {pct(run.confidence)} confidence · started {formatDate(run.startedAt)} · ML: {run.mlFeedbackStatus ?? 'not sent'}</div>
                    </div>
                    <button onClick={() => void loadReplay(run.id)} disabled={!canReplay || loadingReplayId === run.id} className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-400/40 px-3 py-2 text-sm font-semibold text-cyan-100 hover:bg-cyan-500/10 disabled:opacity-60">
                      {loadingReplayId === run.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      Replay
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {activeSubtab === 'replay' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a simulation replay" description="Select a run from Simulation Runs or create a new run from Incident Queue to view the full investigation flight recorder." />
          ) : (
            <>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      {badge(`Incident #${selectedRun.incidentNumber || 'n/a'}`, 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
                      {badge(selectedRun.mode, 'border-indigo-400/40 bg-indigo-400/10 text-indigo-100')}
                      {badge(selectedRun.simulationStatus, statusClass(selectedRun.simulationStatus))}
                      {badge(selectedRun.finalVerdict ? selectedRun.finalVerdict.replace(/_/g, ' ') : 'verdict pending', verdictClass(selectedRun.finalVerdict))}
                    </div>
                    <h2 className="mt-3 text-2xl font-bold text-white">{selectedRun.incidentTitle}</h2>
                    <p className="mt-2 max-w-4xl text-sm text-slate-300">Actual Sentinel classification: <span className="font-semibold text-slate-100">{selectedRun.actualClassification ?? 'Unknown'}</span> · AI simulated verdict: <span className="font-semibold text-cyan-100">{selectedRun.finalVerdict ?? 'n/a'}</span> · Reasoning quality: <span className="font-semibold text-slate-100">{selectedRun.reasoningQuality ?? 'n/a'}</span></p>
                  </div>
                  <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-4 text-center">
                    <div className="text-xs uppercase tracking-wide text-slate-400">Confidence</div>
                    <div className="mt-1 text-3xl font-bold text-cyan-100">{pct(selectedRun.confidence)}</div>
                    <div className="text-xs text-slate-500">Duration {Math.round((selectedRun.durationMs ?? 0) / 1000)}s</div>
                  </div>
                </div>
              </div>

              {canCompareVerdict && (
                <div className="grid gap-3 md:grid-cols-3">
                  <ComparisonCard label="Actual Sentinel" value={selectedRun.actualClassification ?? 'Unknown'} helper={selectedRun.actualDetermination ?? 'No determination available'} />
                  <ComparisonCard label="AI Simulation" value={selectedRun.finalVerdict?.replace(/_/g, ' ') ?? 'n/a'} helper={`${pct(selectedRun.confidence)} confidence · ${selectedRun.reasoningQuality ?? 'quality n/a'}`} />
                  <ComparisonCard label="ML handoff" value={selectedRun.mlFeedbackStatus ?? 'not sent'} helper={selectedRun.mlFeedbackEventId ? `Event ${selectedRun.mlFeedbackEventId}` : 'Use the ML Training Handoff sub-tab'} />
                </div>
              )}

              <ConfidenceEvolution steps={selectedRun.steps} finalConfidence={selectedRun.confidence} />

              <div className="grid min-w-0 gap-4 2xl:grid-cols-[minmax(360px,0.9fr)_minmax(0,1.6fr)]">
                <InvestigationTimeline steps={selectedRun.steps} />

                <div className="min-w-0 space-y-4">
                  <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
                    <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-white"><Sparkles className="h-5 w-5 text-violet-200" /> Reasons & recommendations</div>
                    <div className="space-y-2 text-sm text-slate-300">
                      {(selectedSummary.reasons ?? []).length > 0 ? selectedSummary.reasons?.map((reason, idx) => <div key={`${reason}-${idx}`} className="rounded-lg bg-slate-900/80 p-3">{reason}</div>) : <div className="text-slate-500">No reasons captured.</div>}
                    </div>
                    {(selectedRun.recommendations ?? []).length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Recommendations</div>
                        {selectedRun.recommendations?.map((rec, idx) => <div key={`${rec}-${idx}`} className="rounded-lg border border-slate-700 bg-slate-900/80 p-3 text-sm text-slate-300">{rec}</div>)}
                      </div>
                    )}
                  </div>

                  <QueryTracePanel queries={selectedRun.queries} safeKql={selectedSummary.safeKql} />
                </div>
              </div>

              {canViewEvidence && (
                <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
                  <EvidenceGraphPanel nodes={graphNodes} edges={graphEdges} />
                  <EvidenceCardsPanel evidence={selectedRun.evidence} />
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeSubtab === 'ml' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay first" description="Select or run a simulation to compare actual Sentinel classification, AI verdict, ML Insights score and similar incident clusters." />
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-4">
                <ComparisonCard label="Actual Sentinel" value={selectedRun.actualClassification ?? 'Unknown'} helper={comparison?.actualVsAi?.actualFamily ? `Family: ${comparison.actualVsAi.actualFamily}` : 'Loaded from SecurityIncident'} />
                <ComparisonCard label="AI Simulation" value={selectedRun.finalVerdict?.replace(/_/g, ' ') ?? 'n/a'} helper={`${pct(selectedRun.confidence)} confidence · ${selectedRun.reasoningQuality ?? 'quality n/a'}`} />
                <ComparisonCard label="ML Insights" value={comparison?.mlInsights?.scoreAvailable ? `Priority ${pct(comparison.mlInsights.mlPriorityScore ?? 0)}` : 'not scored'} helper={comparison?.mlInsights?.scoreAvailable ? `FP likelihood ${pct(comparison.mlInsights.falsePositiveLikelihood ?? 0)}` : 'Run/open ML Insights to refresh scores'} />
                <ComparisonCard label="Similarity cluster" value={String(comparison?.similarIncidents?.summary?.similarRunCount ?? 0)} helper={`Avg similarity ${pct(comparison?.similarIncidents?.summary?.averageSimilarity ?? 0)}`} />
              </div>

              <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.72fr)]">
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-lg font-semibold text-white"><Brain className="h-5 w-5 text-violet-200" /> Actual vs AI vs ML verdict comparison</div>
                        <div className="mt-1 text-sm text-slate-400">Compares the stored Sentinel classification, AI simulated verdict and cached ML Insights signal without changing Sentinel.</div>
                      </div>
                      <button onClick={() => selectedRun && void loadComparison(selectedRun.id)} disabled={loadingComparison || !canCompareVerdict} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
                        {loadingComparison ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh comparison
                      </button>
                    </div>
                    <div className="mt-4 grid gap-3 lg:grid-cols-3">
                      <VerdictBox title="Actual Sentinel" value={selectedRun.actualClassification ?? 'Unknown'} family={comparison?.actualVsAi?.actualFamily} />
                      <VerdictBox title="AI Simulation" value={selectedRun.finalVerdict?.replace(/_/g, ' ') ?? 'n/a'} family={comparison?.actualVsAi?.aiFamily} />
                      <VerdictBox title="Agreement" value={comparison?.actualVsAi?.status?.replace(/_/g, ' ') ?? 'pending'} family={comparison?.actualVsAi?.status} helper={`${pct(comparison?.actualVsAi?.score ?? 0)} agreement score`} />
                    </div>
                    {comparison?.actualVsAi?.explanation && <div className="mt-4 rounded-xl border border-slate-700 bg-slate-900/75 p-3 text-sm text-slate-300">{comparison.actualVsAi.explanation}</div>}
                  </div>

                  <SimilarIncidentCluster comparison={comparison} />
                </div>

                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="mt-1 h-6 w-6 text-violet-200" />
                      <div>
                        <h2 className="text-xl font-bold text-white">Feedback capture</h2>
                        <p className="mt-2 text-sm text-slate-300">Capture analyst feedback and optionally send it as a high-priority human signal into ML Insights. Human feedback has higher weight than simulation pseudo-labels.</p>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <select value={feedbackValue} onChange={(e) => setFeedbackValue(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100">
                        <option value="agree_ai">Agree with AI verdict</option>
                        <option value="disagree_ai">Disagree with AI verdict</option>
                        <option value="true_positive">Mark as true positive</option>
                        <option value="false_positive">Mark as false positive</option>
                        <option value="benign_positive">Mark as benign positive</option>
                        <option value="needs_tuning">Needs tuning</option>
                        <option value="needs_human_review">Needs human review</option>
                      </select>
                      <textarea value={feedbackNotes} onChange={(e) => setFeedbackNotes(e.target.value)} rows={4} className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100" placeholder="Optional analyst notes for training/audit trail" />
                      <button onClick={() => void submitFeedback()} disabled={!canCompareVerdict || sendingMl} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-violet-500 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-400 disabled:opacity-60">
                        {sendingMl ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
                        Capture feedback + send human signal to ML
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
                    <div className="text-lg font-semibold text-white">ML Training Handoff</div>
                    <div className="mt-4 grid gap-3">
                      <ComparisonCard label="Weak simulation label" value={selectedRun?.finalVerdict?.replace(/_/g, ' ') ?? 'n/a'} helper={`${pct(selectedRun?.confidence)} confidence`} />
                      <ComparisonCard label="Training weight" value={String(selectedRun?.summary?.mlHandoff?.weight ?? 'n/a')} helper="Weak label, below human feedback" />
                      <ComparisonCard label="Current ML status" value={selectedRun?.mlFeedbackStatus ?? 'not sent'} helper={selectedRun?.mlFeedbackEventId ? `Event ${selectedRun.mlFeedbackEventId}` : 'No weak event id yet'} />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button onClick={() => void sendToMl()} disabled={!selectedRun || !canSendToMl || sendingMl} className="inline-flex items-center gap-2 rounded-lg border border-violet-400/40 px-3 py-2 text-sm font-semibold text-violet-100 hover:bg-violet-500/10 disabled:opacity-60">
                        {sendingMl ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
                        Send weak label
                      </button>
                      <button onClick={() => onNavigate?.('ml_insights', customerId)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">
                        <Brain className="h-4 w-4" /> Open ML Insights
                      </button>
                    </div>
                  </div>

                  <FeedbackSignalsPanel comparison={comparison} />
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {activeSubtab === 'learning' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before starting Learning Mode" description="Learning Mode converts a selected simulation run into a guided SOC analyst training exercise with step explanations, checkpoints and skill scoring." />
          ) : (
            <LearningModePanel
              plan={learningPlan}
              loading={loadingLearning}
              checkpointAnswers={checkpointAnswers}
              setCheckpointAnswers={setCheckpointAnswers}
              answeringCheckpoint={answeringCheckpoint}
              completingLearning={completingLearning}
              onRefresh={() => void loadLearning(selectedRun.id)}
              onSubmitAnswer={submitCheckpointAnswer}
              onComplete={completeLearningMode}
            />
          )}
        </section>
      )}

      {activeSubtab === 'quality' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before viewing quality score" description="Quality Score reviews evidence strength, query coverage, entity coverage, ML agreement and actual-vs-AI alignment for the selected simulation run." />
          ) : (
            <QualityDashboardPanel dashboard={qualityDashboard} loading={loadingQuality} onRefresh={() => void loadQuality(selectedRun.id)} />
          )}
        </section>
      )}

      {activeSubtab === 'clusters' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before deep clustering" description="Deep clustering compares the selected run with previous simulations by title fingerprint, provider type, entities, owner, actual classification and AI verdict." />
          ) : (
            <DeepClusteringPanel clusters={deepClusters} loading={loadingClusters} onRefresh={() => void loadDeepClusters(selectedRun.id)} onOpenRun={(runId) => void loadReplay(runId)} />
          )}
        </section>
      )}

      {activeSubtab === 'tuning' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before detection tuning" description="Detection tuning recommendations are generated from the selected simulation, deep cluster context, verdict agreement and evidence strength." />
          ) : (
            <DetectionTuningPanel tuning={tuningRecommendation} loading={loadingTuning} sending={sendingTuningFinding} canSend={canCompareVerdict} onRefresh={() => void loadTuningRecommendation(selectedRun.id)} onSend={sendTuningFinding} onNavigate={onNavigate} customerId={customerId} />
          )}
        </section>
      )}

      {activeSubtab === 'response' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before response preview" description="Response Preview converts the selected simulation into read-only investigation, containment and notification recommendations. It does not trigger playbooks or update Sentinel." />
          ) : (
            <ResponsePreviewPanel preview={responsePreview} loading={loadingResponsePreview} onRefresh={() => void loadResponsePreview(selectedRun.id)} onNavigate={onNavigate} customerId={customerId} />
          )}
        </section>
      )}

      {activeSubtab === 'case_evidence' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before attaching Case Evidence" description="Case Evidence Pack creates customer-safe reports, replay snapshots and audit-linked artifacts from the selected simulation run." />
          ) : (
            <CaseEvidencePackPanel
              links={caseEvidenceLinks}
              result={caseEvidenceResult}
              loading={loadingCaseEvidence}
              attaching={attachingCaseEvidence}
              canExport={canExport}
              customerVisible={caseEvidenceCustomerVisible}
              setCustomerVisible={setCaseEvidenceCustomerVisible}
              onRefresh={() => void loadCaseEvidenceLinks(selectedRun.id)}
              onAttach={attachCaseEvidencePack}
              onNavigate={onNavigate}
              customerId={customerId}
            />
          )}
        </section>
      )}

      {activeSubtab === 'exports' && (
        <section className="space-y-4">
          {!selectedRun ? (
            <EmptyState title="Open a replay before exporting" description="Exports are generated from the selected simulation run, verdict comparison, safe KQL trace, evidence graph and training context." />
          ) : (
            <>
              <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">Export and training pack</h2>
                    <p className="mt-2 text-sm text-slate-300">Generate export-ready JSON, PDF, analyst training markdown and a customer-safe report. The executive summary uses Azure OpenAI when configured and deterministic fallback otherwise.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {badge('JSON', 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
                    {badge('PDF', 'border-indigo-400/40 bg-indigo-400/10 text-indigo-100')}
                    {badge('Training Pack', 'border-violet-400/40 bg-violet-400/10 text-violet-100')}
                    {badge('Customer-safe', 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100')}
                  </div>
                </div>
                <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <ExportButton label="Export JSON" helper="Full replay, comparison, evidence and report payload" format="json" exportingFormat={exportingFormat} canExport={canExport} onClick={exportRun} />
                  <ExportButton label="Export PDF" helper="Client-side PDF from executive pack" format="pdf_payload" exportingFormat={exportingFormat} canExport={canExport} onClick={exportRun} />
                  <ExportButton label="Analyst Training Pack" helper="Markdown pack for SOC training and QA" format="training_pack" exportingFormat={exportingFormat} canExport={canExport} onClick={exportRun} />
                  <ExportButton label="Customer-safe Report" helper="Markdown report with safe wording" format="customer_safe_report" exportingFormat={exportingFormat} canExport={canExport} onClick={exportRun} />
                </div>
              </div>

              {exportPack ? <ExportPreview payload={exportPack} /> : <EmptyState title="No export generated yet" description="Click one of the export buttons to generate the AI executive summary, training pack and customer-safe report preview." />}
            </>
          )}
        </section>
      )}

      {activeSubtab === 'settings' && (
        <section className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="mb-4 flex items-center gap-2 text-lg font-semibold text-white"><ShieldCheck className="h-5 w-5 text-cyan-200" /> Enforced read-only operations</div>
            <div className="grid gap-2 text-sm text-slate-300">
              {['Read Sentinel incidents', 'Read alert timeline', 'Extract entities', 'Generate investigation plan', 'Store replay evidence', 'Generate simulated verdict', 'Send weak label to ML Insights'].map((item) => <div key={item}><Guardrail text={item} positive /></div>)}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="mb-4 flex items-center gap-2 text-lg font-semibold text-white"><AlertTriangle className="h-5 w-5 text-amber-200" /> Explicitly blocked</div>
            <div className="grid gap-2 text-sm text-slate-300">
              {['Close or update Sentinel incidents', 'Assign owners', 'Add tags or comments', 'Trigger playbooks', 'Create automation rules', 'Modify watchlists', 'Create bookmarks'].map((item) => <div key={item}><Guardrail text={item} /></div>)}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5 lg:col-span-2">
            <div className="text-lg font-semibold text-white">Permission model</div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              {['simulation_lab.read', 'simulation_lab.run', 'simulation_lab.replay', 'simulation_lab.view_evidence', 'simulation_lab.compare_verdict', 'simulation_lab.send_to_ml', 'simulation_lab.export', 'simulation_lab.admin'].map((perm) => (
                <div key={perm} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3 text-sm text-slate-300">{perm}</div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function HeaderBlock({ customerName, customerId, customerOptions, onCustomerChange }: { customerName?: string; customerId?: string; customerOptions?: Array<{ id: string; name: string; enabled?: boolean }>; onCustomerChange?: (customerId: string) => void }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-700/80 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.26),transparent_30%),linear-gradient(135deg,rgba(15,23,42,0.95),rgba(30,41,59,0.85))] p-6 shadow-2xl shadow-slate-950/30">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100">
            <Sparkles className="h-3.5 w-3.5" /> IncidentOps
          </div>
          <h1 className="mt-3 text-3xl font-bold text-white">AI Simulation Lab</h1>
          <p className="mt-2 max-w-4xl text-sm text-slate-300">Replay real Sentinel incidents through a transparent, read-only AI investigation pipeline. Track each hypothesis, evidence item, generated plan, verdict and ML training handoff without changing Sentinel.</p>
        </div>
        <div className="min-w-[260px] rounded-2xl border border-slate-700/80 bg-slate-950/60 p-4 text-sm text-slate-300">
          <div className="text-xs uppercase tracking-wide text-slate-500">Current customer</div>
          {customerOptions && customerOptions.length > 0 ? (
            <select
              value={customerId ?? ''}
              onChange={(event) => onCustomerChange?.(event.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-semibold text-white outline-none focus:border-cyan-400"
            >
              {!customerId && <option value="">Select customer</option>}
              {customerOptions.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
            </select>
          ) : (
            <div className="mt-1 text-lg font-semibold text-white">{customerName ?? 'No customer selected'}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, helper }: { icon: ReactNode; label: string; value: string | number; helper: string }) {
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/55 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">{icon}{label}</div>
      <div className="mt-2 text-2xl font-bold text-white">{value}</div>
      <div className="mt-1 text-xs text-slate-400">{helper}</div>
    </div>
  );
}

function ComparisonCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/55 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-lg font-bold capitalize text-white">{value}</div>
      <div className="mt-1 text-xs text-slate-400">{helper}</div>
    </div>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <pre className="max-h-44 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-800 bg-black/30 p-3 text-xs leading-relaxed text-slate-300"><code>{text(value, 1600)}</code></pre>
    </div>
  );
}


function summarizeOutput(value: Record<string, unknown>): string {
  const keys = Object.keys(value ?? {});
  if (!keys.length) return "No structured output recorded.";
  const priority = ["alertCount", "uniqueEntityCount", "completed", "warnings", "blocked", "rowsReturned", "finalVerdict", "confidence", "reasoningQuality", "graphNodes", "graphEdges"];
  const parts = priority.filter((key) => key in value).map((key) => `${key}: ${text(value[key], 80)}`);
  return (parts.length ? parts : keys.slice(0, 4).map((key) => `${key}: ${text(value[key], 80)}`)).join(" · ");
}

function confidenceSeries(steps: SimulationStep[], finalConfidence: number): Array<{ order: number; title: string; status: StepStatus; impact: number; confidence: number }> {
  let running = 0.42;
  const series = steps.map((step) => {
    const impact = Number(step.confidenceImpact ?? 0);
    const boundedImpact = step.type === "verdict_generated" ? 0 : Math.max(-0.12, Math.min(0.16, impact));
    running = Math.max(0.05, Math.min(0.95, running + boundedImpact));
    return { order: step.order, title: step.title, status: step.status, impact, confidence: running };
  });
  if (series.length) series[series.length - 1].confidence = finalConfidence <= 1 ? finalConfidence : finalConfidence / 100;
  return series;
}

function ConfidenceEvolution({ steps, finalConfidence }: { steps: SimulationStep[]; finalConfidence: number }) {
  const series = confidenceSeries(steps, finalConfidence);
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-white">Confidence evolution</div>
          <div className="text-xs text-slate-400">Shows how each read-only step changed the simulated confidence. It is explanatory only, not a Sentinel action.</div>
        </div>
        <div className="text-sm font-semibold text-cyan-100">Final {pct(finalConfidence)}</div>
      </div>
      <div className="grid gap-2 md:grid-cols-3 xl:grid-cols-6">
        {series.map((item) => (
          <div key={`${item.order}-${item.title}`} className="min-w-0 rounded-xl border border-slate-700 bg-slate-900/70 p-3">
            <div className="flex items-center justify-between gap-2 text-xs text-slate-400">
              <span>Step {item.order}</span>
              <span className={item.impact >= 0 ? "text-emerald-300" : "text-amber-300"}>{item.impact >= 0 ? "+" : ""}{pct(item.impact)}</span>
            </div>
            <div className="mt-2 truncate text-sm font-semibold text-white" title={item.title}>{item.title}</div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-cyan-400" style={{ width: `${Math.max(4, Math.min(100, item.confidence * 100))}%` }} />
            </div>
            <div className="mt-1 text-right text-xs text-slate-400">{pct(item.confidence)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvestigationTimeline({ steps }: { steps: SimulationStep[] }) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
      <div className="mb-4 flex items-center gap-2 text-lg font-semibold text-white"><GitBranch className="h-5 w-5 text-cyan-200" /> Investigation timeline</div>
      <div className="relative space-y-3 before:absolute before:left-5 before:top-3 before:h-[calc(100%-1.5rem)] before:w-px before:bg-slate-700/80">
        {steps.map((step) => <TimelineStepCard key={step.id} step={step} />)}
      </div>
    </div>
  );
}

function TimelineStepCard({ step }: { step: SimulationStep }) {
  return (
    <div className="relative min-w-0 pl-12">
      <div className="absolute left-0 top-1 inline-flex h-10 w-10 items-center justify-center rounded-full border border-cyan-400/40 bg-slate-950 text-sm font-bold text-cyan-100 shadow-lg shadow-cyan-950/40">{step.order}</div>
      <div className="min-w-0 rounded-xl border border-slate-700 bg-slate-900/70 p-4">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="break-words text-base font-semibold text-white">{step.title}</div>
            <div className="mt-1 text-xs text-slate-400">{step.type} · {step.durationMs ?? 0} ms · confidence impact {pct(step.confidenceImpact)}</div>
          </div>
          <div className="shrink-0">{badge(step.status, statusClass(step.status))}</div>
        </div>
        <div className="mt-3 rounded-lg border border-slate-800 bg-black/20 p-3 text-xs leading-relaxed text-slate-300">
          {summarizeOutput(step.output)}
        </div>
        <details className="mt-3 rounded-lg border border-slate-800 bg-slate-950/50 p-3 text-xs text-slate-300">
          <summary className="cursor-pointer select-none font-semibold text-slate-200">Show structured output and evidence preview</summary>
          <div className="mt-3 grid min-w-0 gap-3">
            <JsonBlock label="Output" value={step.output} />
            <JsonBlock label="Evidence preview" value={step.evidence?.slice?.(0, 4) ?? step.evidence} />
          </div>
        </details>
      </div>
    </div>
  );
}

function QueryTracePanel({ queries, safeKql }: { queries: SimulationQuery[]; safeKql?: SimulationSummary["safeKql"] }) {
  const completed = queries.filter((query) => query.status === "completed").length;
  const warning = queries.filter((query) => query.status === "warning").length;
  const blocked = queries.filter((query) => query.status === "skipped").length;
  const rows = queries.reduce((sum, query) => sum + Number(query.rowsReturned ?? 0), 0);
  return (
    <div className="min-w-0 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-lg font-semibold text-white"><Database className="h-5 w-5 text-cyan-200" /> Safe KQL execution trace</div>
          <div className="mt-1 text-xs text-slate-400">AI-generated query plan with read-only guardrails, bounded lookbacks, explicit take limits and no Sentinel write operations.</div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 text-xs">
          {badge(`${safeKql?.generated ?? queries.length} generated`, 'border-slate-600 bg-slate-800 text-slate-200')}
          {badge(`${completed} completed`, 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100')}
          {warning > 0 && badge(`${warning} warning`, 'border-amber-500/40 bg-amber-500/10 text-amber-100')}
          {blocked > 0 && badge(`${blocked} blocked`, 'border-red-500/40 bg-red-500/10 text-red-100')}
          {badge(`${rows} rows`, 'border-cyan-500/40 bg-cyan-500/10 text-cyan-100')}
        </div>
      </div>
      <div className="min-w-0 space-y-3">
        {queries.map((query) => <QueryTraceCard key={query.id} query={query} />)}
        {queries.length === 0 && <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 text-sm text-slate-400">No query trace has been captured for this run.</div>}
      </div>
    </div>
  );
}

function QueryTraceCard({ query }: { query: SimulationQuery }) {
  const guardrailReasons = Array.isArray(query.guardrailReasons) ? query.guardrailReasons : [];
  const preview = Array.isArray(query.resultPreview) ? query.resultPreview : [];
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-slate-700 bg-slate-900/70 p-3">
      <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="break-words font-semibold text-slate-100">{query.purpose}</div>
          <div className="mt-1 text-xs text-slate-400">Tables: {(query.tables ?? []).join(', ') || 'n/a'} · {query.executionTimeMs ?? 0} ms · Guardrail: {query.guardrailStatus ?? 'allowed'}</div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {badge(query.status, statusClass(query.status))}
          {badge(`${query.rowsReturned ?? 0} rows`, 'border-slate-600 bg-slate-800 text-slate-200')}
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-slate-800 bg-black/20 p-3 text-sm leading-relaxed text-slate-300">{query.resultSummary}</div>
      {guardrailReasons.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {guardrailReasons.slice(0, 4).map((reason, idx) => <span key={`${query.id}-guard-${idx}`} className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-[11px] text-cyan-100">{reason}</span>)}
        </div>
      )}
      {query.error && <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-100">{query.error}</div>}
      <KqlCodeBlock kql={query.kql} />
      {preview.length > 0 && <DataPreviewTable rows={preview} />}
    </div>
  );
}

function KqlCodeBlock({ kql }: { kql: string }) {
  return (
    <div className="mt-3 min-w-0 overflow-hidden rounded-lg border border-slate-800 bg-black/40">
      <div className="border-b border-slate-800 px-3 py-2 text-xs uppercase tracking-wide text-slate-500">Generated safe KQL</div>
      <pre className="max-h-72 max-w-full overflow-auto whitespace-pre-wrap break-words p-3 text-xs leading-relaxed text-cyan-100"><code>{kql}</code></pre>
    </div>
  );
}

function DataPreviewTable({ rows }: { rows: Array<Record<string, unknown>> }) {
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).slice(0, 8);
  if (!columns.length) return null;
  return (
    <div className="mt-3 min-w-0 overflow-hidden rounded-lg border border-slate-800">
      <div className="border-b border-slate-800 px-3 py-2 text-xs uppercase tracking-wide text-slate-500">Result preview</div>
      <div className="max-w-full overflow-auto">
        <table className="min-w-full divide-y divide-slate-800 text-xs">
          <thead className="bg-slate-950/80 text-slate-400">
            <tr>{columns.map((column) => <th key={column} className="px-3 py-2 text-left font-semibold">{column}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-800/80">
            {rows.slice(0, 5).map((row, rowIdx) => (
              <tr key={rowIdx}>
                {columns.map((column) => <td key={`${rowIdx}-${column}`} className="max-w-[220px] truncate px-3 py-2 text-slate-300" title={text(row[column], 500)}>{text(row[column], 120)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function toneClass(tone?: string): string {
  const normalized = (tone ?? '').toLowerCase();
  if (normalized === 'red') return 'border-red-500/40 bg-red-500/10 text-red-100';
  if (normalized === 'green') return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100';
  if (normalized === 'amber') return 'border-amber-500/40 bg-amber-500/10 text-amber-100';
  if (normalized === 'purple') return 'border-violet-500/40 bg-violet-500/10 text-violet-100';
  if (normalized === 'blue') return 'border-cyan-500/40 bg-cyan-500/10 text-cyan-100';
  return 'border-slate-600 bg-slate-800 text-slate-200';
}

function EvidenceGraphPanel({ nodes, edges }: { nodes: EntityGraphNode[]; edges: EntityGraphEdge[] }) {
  const incident = nodes.find((node) => node.type === 'incident') ?? nodes[0];
  const verdict = nodes.find((node) => node.type === 'verdict');
  const alerts = nodes.filter((node) => node.type === 'alert').slice(0, 8);
  const entities = nodes.filter((node) => node.type !== 'incident' && node.type !== 'alert' && node.type !== 'verdict').slice(0, 18);
  return (
    <div className="min-w-0 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-2 text-lg font-semibold text-white"><GitBranch className="h-5 w-5 text-emerald-200" /> Evidence graph</div>
        <div className="text-xs text-slate-500">{nodes.length} nodes · {edges.length} relationships</div>
      </div>
      {nodes.length === 0 ? (
        <div className="text-sm text-slate-500">No graph nodes were stored for this replay.</div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/10 p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-cyan-200">Incident</div>
            <div className="mt-1 break-words text-base font-semibold text-white">{incident?.label ?? incident?.value ?? incident?.id ?? 'Incident'}</div>
          </div>
          <div className="grid gap-3 lg:grid-cols-3">
            <GraphGroup title="Alerts" nodes={alerts} empty="No alert nodes" />
            <GraphGroup title="Entities" nodes={entities} empty="No entity nodes" />
            <GraphGroup title="Verdict" nodes={verdict ? [verdict] : []} empty="No verdict node" />
          </div>
        </div>
      )}
    </div>
  );
}

function GraphGroup({ title, nodes, empty }: { title: string; nodes: EntityGraphNode[]; empty: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-700 bg-slate-900/70 p-3">
      <div className="mb-2 text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="flex max-h-72 flex-wrap gap-2 overflow-auto pr-1">
        {nodes.map((node, idx) => <span key={`${node.id ?? node.label ?? idx}`} className={`max-w-full rounded-full border px-2.5 py-1 text-xs ${toneClass(node.tone)}`} title={node.label ?? node.value ?? node.id}>{text(node.label ?? node.value ?? node.id, 72)}</span>)}
        {nodes.length === 0 && <span className="text-xs text-slate-500">{empty}</span>}
      </div>
    </div>
  );
}

function EvidenceCardsPanel({ evidence }: { evidence: SimulationEvidence[] }) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
      <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-white"><Eye className="h-5 w-5 text-cyan-200" /> Evidence cards</div>
      <div className="max-h-[620px] min-w-0 space-y-2 overflow-auto pr-1">
        {evidence.map((item) => (
          <div key={item.id} className="min-w-0 rounded-xl border border-slate-700 bg-slate-900/75 p-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="break-words font-semibold text-white">{item.title}</span>
              {item.severity && badge(item.severity, severityClass(item.severity))}
            </div>
            <div className="mt-1 break-words text-xs text-slate-400">{item.type} · {item.source ?? 'source n/a'} {item.entityType && item.entityValue ? `· ${item.entityType}: ${item.entityValue}` : ''}</div>
            <div className="mt-2 rounded-lg bg-black/20 p-2 text-xs leading-relaxed text-slate-300">{text(item.details, 420)}</div>
          </div>
        ))}
        {evidence.length === 0 && <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm text-slate-500">No evidence cards were stored for this replay.</div>}
      </div>
    </div>
  );
}


function VerdictBox({ title, value, family, helper }: { title: string; value: string; family?: string; helper?: string }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="mt-2 break-words text-lg font-bold capitalize text-white">{value}</div>
      <div className="mt-2 flex flex-wrap gap-2">
        {family && badge(family.replace(/_/g, ' '), verdictClass(family))}
        {helper && <span className="text-xs text-slate-400">{helper}</span>}
      </div>
    </div>
  );
}

function SimilarIncidentCluster({ comparison }: { comparison: SimulationComparison | null }) {
  const runs = comparison?.similarIncidents?.runs ?? [];
  const summary = comparison?.similarIncidents?.summary;
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-white">Similar incident clustering</div>
          <div className="mt-1 text-sm text-slate-400">Clusters previous Simulation Lab runs by title tokens, entity graph overlap and verdict/classification family.</div>
        </div>
        <div className="text-xs text-slate-400">{summary?.similarRunCount ?? 0} similar · {summary?.likelyNoisePatternCount ?? 0} benign/noise · {summary?.likelyTpPatternCount ?? 0} TP-like</div>
      </div>
      <div className="mt-4 space-y-2">
        {runs.map((run, idx) => (
          <div key={String(run.id ?? idx)} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="break-words font-semibold text-white">#{text(run.incidentNumber, 80)} · {text(run.incidentTitle, 180)}</div>
                <div className="mt-1 text-xs text-slate-400">Actual: {text(run.actualClassification, 80) || 'Unknown'} · AI: {text(run.finalVerdict, 80) || 'n/a'} · {formatDate(text(run.startedAt, 80))}</div>
              </div>
              {badge(`${pct(Number(run.similarityScore ?? 0))} similar`, 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
            </div>
            {Array.isArray(run.similarityReasons) && <div className="mt-2 flex flex-wrap gap-2">{run.similarityReasons.slice(0, 4).map((reason: unknown, rIdx: number) => <span key={rIdx} className="rounded-full border border-slate-700 bg-slate-950 px-2 py-1 text-[11px] text-slate-300">{text(reason, 120)}</span>)}</div>}
          </div>
        ))}
        {runs.length === 0 && <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm text-slate-500">No similar simulation runs found yet. Clustering improves as more incidents are simulated.</div>}
      </div>
    </div>
  );
}

function FeedbackSignalsPanel({ comparison }: { comparison: SimulationComparison | null }) {
  const signals = comparison?.mlInsights?.feedbackSignals ?? [];
  const human = comparison?.analystFeedback ?? [];
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
      <div className="text-lg font-semibold text-white">Training signals</div>
      <div className="mt-1 text-sm text-slate-400">Recent ML feedback and analyst signals linked to this incident.</div>
      <div className="mt-4 space-y-2">
        {human.slice(0, 4).map((item, idx) => (
          <div key={`human-${String(item.id ?? idx)}`} className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-50">
            <div className="font-semibold">Human: {text(item.feedback, 80)} → {text(item.analystVerdict, 80)}</div>
            <div className="mt-1 text-xs text-emerald-100/80">{formatDate(text(item.createdAt, 80))} · {text(item.notes, 220)}</div>
          </div>
        ))}
        {signals.slice(0, 5).map((item, idx) => (
          <div key={`signal-${String(item.id ?? idx)}`} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3 text-sm text-slate-300">
            <div className="font-semibold">{text(item.signalSource, 80)}: {text(item.feedback, 80)} · weight {text(item.weight, 20)}</div>
            <div className="mt-1 text-xs text-slate-500">{formatDate(text(item.createdAt, 80))} · {text(item.reason, 220)}</div>
          </div>
        ))}
        {signals.length === 0 && human.length === 0 && <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm text-slate-500">No training signals have been captured for this run yet.</div>}
      </div>
    </div>
  );
}

function LearningModePanel({ plan, loading, checkpointAnswers, setCheckpointAnswers, answeringCheckpoint, completingLearning, onRefresh, onSubmitAnswer, onComplete }: {
  plan: LearningPlan | null;
  loading: boolean;
  checkpointAnswers: Record<string, string>;
  setCheckpointAnswers: (updater: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  answeringCheckpoint: string | null;
  completingLearning: boolean;
  onRefresh: () => void;
  onSubmitAnswer: (checkpointId: string) => Promise<void>;
  onComplete: () => Promise<void>;
}) {
  const modules = plan?.modules ?? [];
  const summary = plan?.summary ?? {};
  const answered = Number(summary.answeredCheckpoints ?? 0);
  const total = Number(summary.totalCheckpoints ?? modules.length);
  const finalScore = Number(summary.finalScore ?? 0);
  const completion = summary.completion;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-violet-400/30 bg-violet-500/10 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <BookOpen className="mt-1 h-6 w-6 text-violet-200" />
            <div>
              <h2 className="text-xl font-bold text-white">Analyst Learning Mode</h2>
              <p className="mt-2 max-w-4xl text-sm text-violet-50/90">Guided investigation training built from the selected simulation replay. Each checkpoint explains why the step matters, asks the analyst to reason through the evidence, and stores completion for SOC training records.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(plan?.skillTags ?? []).map((tag) => badge(tag, 'border-violet-400/40 bg-violet-400/10 text-violet-100'))}
                {!plan?.skillTags?.length && badge('investigation fundamentals', 'border-slate-600 bg-slate-900 text-slate-200')}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
            <button onClick={() => void onComplete()} disabled={completingLearning || total === 0 || answered === 0} className="inline-flex items-center gap-2 rounded-lg bg-violet-500 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-400 disabled:opacity-60">
              {completingLearning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Award className="h-4 w-4" />}
              Complete training
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <ComparisonCard label="Checkpoints" value={`${answered}/${total}`} helper="Answered vs available" />
        <ComparisonCard label="Analyst score" value={answered ? pct(finalScore) : 'n/a'} helper={String(summary.finalLevel ?? 'Pending')} />
        <ComparisonCard label="Training status" value={completion ? 'completed' : 'in progress'} helper={completion ? `Completed ${formatDate(text(completion.completedAt, 80))}` : 'Stored after completion'} />
        <ComparisonCard label="Skill tags" value={String(plan?.skillTags?.length ?? 0)} helper={(plan?.skillTags ?? []).slice(0, 3).join(', ') || 'Generated from replay'} />
      </div>

      <div className="space-y-3">
        {modules.map((module) => (
          <CheckpointCard
            key={module.checkpointId}
            module={module}
            value={checkpointAnswers[module.checkpointId] ?? ''}
            busy={answeringCheckpoint === module.checkpointId}
            onChange={(value) => setCheckpointAnswers((prev) => ({ ...prev, [module.checkpointId]: value }))}
            onSubmit={() => void onSubmitAnswer(module.checkpointId)}
          />
        ))}
        {modules.length === 0 && <EmptyState title="No learning checkpoints generated" description="Open a completed simulation replay with timeline steps to generate Learning Mode checkpoints." />}
      </div>
    </div>
  );
}

function CheckpointCard({ module, value, busy, onChange, onSubmit }: { module: LearningCheckpoint; value: string; busy: boolean; onChange: (value: string) => void; onSubmit: () => void }) {
  const answered = typeof module.score === 'number';
  return (
    <div className="rounded-2xl border border-slate-700/80 bg-slate-950/55 p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {badge(`Step ${module.stepOrder}`, 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
            {badge(module.status || 'status n/a', statusClass(module.status))}
            {answered && badge(`${pct(module.score)} score`, module.isCorrect ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100' : 'border-amber-400/40 bg-amber-400/10 text-amber-100')}
          </div>
          <h3 className="mt-3 break-words text-lg font-semibold text-white">{module.title}</h3>
          <div className="mt-1 text-xs text-slate-500">{module.stepType} · confidence impact {pct(module.confidenceImpact ?? 0)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {module.skillTags.map((tag) => badge(tag, 'border-violet-400/40 bg-violet-400/10 text-violet-100'))}
        </div>
      </div>
      <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Why this step matters</div>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{module.whyItMatters}</p>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">Expected answer</div>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{module.expectedAnswer}</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-semibold text-white">Checkpoint</label>
            <p className="mt-1 text-sm text-slate-300">{module.question}</p>
            <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100" placeholder="Write the analyst reasoning you would expect during triage..." />
            <button onClick={onSubmit} disabled={busy || !value.trim()} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-cyan-400 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
              Submit checkpoint
            </button>
          </div>
          {answered && (
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-sm text-emerald-50">
              <div className="font-semibold">Stored answer: {module.isCorrect ? 'meets expectation' : 'needs improvement'}</div>
              <div className="mt-1 text-emerald-100/90">{module.feedback}</div>
              {module.analystAnswer && <div className="mt-2 rounded-lg bg-black/20 p-2 text-xs text-emerald-50/90">{module.analystAnswer}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QualityDashboardPanel({ dashboard, loading, onRefresh }: { dashboard: QualityDashboard | null; loading: boolean; onRefresh: () => void }) {
  if (!dashboard && loading) return <EmptyState title="Loading quality dashboard" description="Calculating evidence, query, entity, ML and verdict agreement scores." />;
  if (!dashboard) return <EmptyState title="No quality dashboard loaded" description="Open a simulation run and refresh the quality score to calculate the dashboard." />;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <BarChart3 className="mt-1 h-6 w-6 text-cyan-200" />
            <div>
              <h2 className="text-xl font-bold text-white">Simulation Quality & Reasoning Score</h2>
              <p className="mt-2 max-w-4xl text-sm text-cyan-50/90">This dashboard scores how well the simulation is supported. A high score means the replay is evidence-backed and reviewable; it does not mean the AI verdict should be accepted without analyst judgment.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {badge(`${pct(dashboard.overallScore)} overall`, dashboard.overallLevel === 'High' ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100' : dashboard.overallLevel === 'Medium' ? 'border-amber-400/40 bg-amber-400/10 text-amber-100' : 'border-red-400/40 bg-red-400/10 text-red-100')}
            <button onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh score
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {dashboard.metrics.map((item) => <QualityMetricCard key={item.id} metric={item} />)}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
        <ConfidenceQualityTimeline timeline={dashboard.confidenceTimeline} />
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Recommended action</div>
            <div className="mt-3 rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm leading-relaxed text-slate-300">{dashboard.recommendedAction}</div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Warnings & scoring notes</div>
            <div className="mt-3 space-y-2 text-sm text-slate-300">
              {(dashboard.riskWarnings ?? []).map((item, idx) => <div key={`warn-${idx}`} className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-amber-50">{item}</div>)}
              {(dashboard.scoringNotes ?? []).map((item, idx) => <div key={`note-${idx}`} className="rounded-lg border border-slate-700 bg-slate-900/75 p-3">{item}</div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QualityMetricCard({ metric }: { metric: QualityMetric }) {
  const cls = metric.level === 'High' ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100' : metric.level === 'Medium' ? 'border-amber-400/40 bg-amber-400/10 text-amber-100' : 'border-red-400/40 bg-red-400/10 text-red-100';
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-white">{metric.label}</div>
        {badge(metric.level, cls)}
      </div>
      <div className="mt-3 h-2 rounded-full bg-slate-800">
        <div className="h-2 rounded-full bg-cyan-400" style={{ width: `${Math.max(4, Math.min(100, Math.round((metric.score || 0) * 100)))}%` }} />
      </div>
      <div className="mt-2 text-2xl font-bold text-white">{pct(metric.score)}</div>
      <div className="mt-2 text-xs leading-relaxed text-slate-400">{metric.helper}</div>
    </div>
  );
}

function ConfidenceQualityTimeline({ timeline }: { timeline: QualityDashboard['confidenceTimeline'] }) {
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
      <div className="text-lg font-semibold text-white">Why confidence changed</div>
      <div className="mt-4 space-y-3">
        {timeline.map((item, idx) => (
          <div key={`${item.stepOrder}-${idx}`} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="break-words font-semibold text-white">{String(item.stepOrder ?? idx + 1)} · {text(item.title, 220)}</div>
                <div className="mt-1 text-xs text-slate-500">{text(item.stepType, 120)} · {text(item.status, 80)} · impact {pct(item.impact ?? 0)}</div>
              </div>
              {badge(pct(item.confidence ?? 0), 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
            </div>
            <div className="mt-2 text-sm text-slate-300">{item.explanation}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeepClusteringPanel({ clusters, loading, onRefresh, onOpenRun }: { clusters: DeepClusters | null; loading: boolean; onRefresh: () => void; onOpenRun: (runId: string) => void }) {
  if (!clusters && loading) return <EmptyState title="Loading deep clusters" description="Comparing previous simulation runs across title, provider type, entities, owner and verdict families." />;
  if (!clusters) return <EmptyState title="No deep cluster loaded" description="Open a completed simulation run and refresh deep clustering." />;
  const summary = clusters.summary ?? {};
  const candidates = clusters.candidates ?? [];
  const recurring = clusters.recurringFalsePositivePatterns ?? [];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-500/10 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <GitBranch className="mt-1 h-6 w-6 text-fuchsia-200" />
            <div>
              <h2 className="text-xl font-bold text-white">Similar Incident Deep Clustering</h2>
              <p className="mt-2 max-w-4xl text-sm text-fuchsia-50/90">Clusters previous Simulation Lab runs by incident title fingerprint, alert/provider type, entities, owner, actual classification and AI simulated verdict. This is read-only and does not tune Sentinel automatically.</p>
            </div>
          </div>
          <button onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh clusters
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <ComparisonCard label="Cluster size" value={String(summary.totalClusteredIncidents ?? 0)} helper={`${String(summary.highSimilarityIncidents ?? 0)} high-similarity`} />
        <ComparisonCard label="Benign actual" value={String(summary.benignActualCount ?? 0)} helper="Closed benign / FP family" />
        <ComparisonCard label="AI benign" value={String(summary.benignAiVerdictCount ?? 0)} helper="AI benign verdict family" />
        <ComparisonCard label="AI match" value={String(summary.aiVerdictMatchCount ?? 0)} helper="Same AI verdict family" />
        <ComparisonCard label="Avg similarity" value={pct(Number(summary.averageSimilarity ?? 0))} helper={summary.recurringFalsePositivePattern ? 'Recurring FP pattern' : 'Pattern still forming'} />
      </div>

      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
        <div className="text-lg font-semibold text-white">Cluster narrative</div>
        <div className="mt-3 rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm leading-relaxed text-slate-300">{String(summary.narrative ?? 'No cluster narrative generated.')}</div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Cluster dimensions</div>
            <div className="mt-4 space-y-3">
              {(clusters.clusters ?? []).map((item) => (
                <div key={String(item.id)} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-white">{item.label}</div>
                      <div className="mt-1 text-xs text-slate-400">{item.helper}</div>
                    </div>
                    {badge(String(item.count ?? 0), 'border-fuchsia-400/40 bg-fuchsia-400/10 text-fuchsia-100')}
                  </div>
                  <div className="mt-2 break-words text-xs text-slate-500">{item.value || 'n/a'}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Recurring FP patterns</div>
            <div className="mt-3 space-y-2 text-sm text-slate-300">
              {recurring.length ? recurring.map((item, idx) => <div key={idx} className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-amber-50"><div className="font-semibold">{String(item.title ?? 'Recurring pattern')}</div><div className="mt-1 text-xs">{String(item.recommendation ?? '')}</div></div>) : <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-3 text-slate-500">No strong recurring false-positive pattern yet.</div>}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
          <div className="text-lg font-semibold text-white">Similar incidents</div>
          <div className="mt-4 max-h-[680px] overflow-auto rounded-xl border border-slate-800">
            {candidates.length ? candidates.map((item) => (
              <button key={String(item.id)} onClick={() => item.id && onOpenRun(String(item.id))} className="block w-full border-b border-slate-800 bg-slate-950/40 p-4 text-left hover:bg-slate-900/80">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="break-words font-semibold text-white">#{item.incidentNumber ?? 'n/a'} · {text(item.incidentTitle, 160)}</div>
                    <div className="mt-1 text-xs text-slate-400">Actual: {item.actualClassification ?? 'Unknown'} · AI: {String(item.finalVerdict ?? 'n/a').replace(/_/g, ' ')} · {formatDate(item.startedAt)}</div>
                  </div>
                  {badge(pct(item.similarityScore ?? 0), 'border-fuchsia-400/40 bg-fuchsia-400/10 text-fuchsia-100')}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
                  {(item.similarityReasons ?? []).slice(0, 4).map((reason, idx) => <span key={idx} className="rounded-full border border-slate-700 bg-slate-900 px-2 py-1">{reason}</span>)}
                </div>
              </button>
            )) : <div className="p-6 text-center text-sm text-slate-500">No similar completed simulation runs found.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetectionTuningPanel({ tuning, loading, sending, canSend, onRefresh, onSend, onNavigate, customerId }: { tuning: TuningRecommendation | null; loading: boolean; sending: boolean; canSend: boolean; onRefresh: () => void; onSend: () => Promise<void>; onNavigate?: (tabId: string, customerId?: string) => void; customerId?: string }) {
  if (!tuning && loading) return <EmptyState title="Loading detection tuning recommendation" description="Reviewing cluster context, low-value incident signals, suppression options and safe KQL tuning draft." />;
  if (!tuning) return <EmptyState title="No tuning recommendation loaded" description="Open a completed simulation run and refresh the tuning recommendation." />;
  const dq = tuning.detectionQuality ?? {};
  const socDebt = tuning.socDebt ?? {};
  const recs = tuning.recommendations ?? [];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <Target className="mt-1 h-6 w-6 text-amber-200" />
            <div>
              <h2 className="text-xl font-bold text-white">Detection Tuning Recommendation</h2>
              <p className="mt-2 max-w-4xl text-sm text-amber-50/90">Converts Simulation Lab clustering into detection-quality and SOC-debt recommendations. It generates review guidance only; no Sentinel rule, watchlist or suppression is changed automatically.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh tuning
            </button>
            <button onClick={() => void onSend()} disabled={!canSend || sending} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
              Send finding
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <ComparisonCard label="Low-value score" value={pct(Number(dq.lowValueIncidentScore ?? 0))} helper={`Severity ${String(dq.severity ?? 'Low')}`} />
        <ComparisonCard label="Noisy pattern" value={dq.noisyPatternDetected ? 'yes' : 'no'} helper={`${String(dq.recurringClusterSize ?? 0)} clustered run(s)`} />
        <ComparisonCard label="Benign actual" value={String(dq.benignActualCount ?? 0)} helper="Similar benign outcomes" />
        <ComparisonCard label="SOC Debt impact" value={String(socDebt.debtImpact ?? 'Low')} helper={String(socDebt.debtType ?? 'tuning review')} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Recommended action</div>
            <div className="mt-3 rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm leading-relaxed text-slate-300">{String(dq.recommendedAction ?? 'Continue monitoring until more evidence is available.')}</div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Recommendations</div>
            <div className="mt-4 space-y-3">
              {recs.map((rec, idx) => (
                <div key={String(rec.id ?? idx)} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-semibold text-white">{String(rec.title ?? 'Recommendation')}</div>
                    {badge(String(rec.priority ?? 'Medium'), String(rec.priority ?? '').toLowerCase() === 'high' ? 'border-red-400/40 bg-red-400/10 text-red-100' : 'border-amber-400/40 bg-amber-400/10 text-amber-100')}
                  </div>
                  <div className="mt-2 text-sm leading-relaxed text-slate-300">{String(rec.details ?? rec.rationale ?? '')}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Suppression / watchlist options</div>
            <div className="mt-3 space-y-2 text-sm text-slate-300">
              {(tuning.suppressionOptions ?? []).map((item, idx) => <div key={idx} className="rounded-lg border border-slate-700 bg-slate-900/75 p-3">{item}</div>)}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-white">Safe KQL tuning draft</div>
              <button onClick={() => downloadTextFile('simulation-lab-safe-tuning-draft.kql', tuning.safeKqlTuningDraft || '', 'text/plain;charset=utf-8')} className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-slate-800">Download KQL</button>
            </div>
            <pre className="mt-4 max-h-[620px] max-w-full overflow-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-black/40 p-4 text-xs leading-relaxed text-cyan-50">{tuning.safeKqlTuningDraft || '// No KQL draft generated.'}</pre>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="text-lg font-semibold text-white">Send to operations</div>
            <p className="mt-2 text-sm text-slate-300">Stores this finding for Detection Quality Score and SOC Debt review queues. This does not alter analytic rules, watchlists, suppression rules, or Sentinel incidents.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => void onSend()} disabled={!canSend || sending} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
                Store finding
              </button>
              <button onClick={() => onNavigate?.('detection_quality', customerId)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">Open Detection Quality</button>
              <button onClick={() => onNavigate?.('soc_debt', customerId)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">Open SOC Debt</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function ResponsePreviewPanel({ preview, loading, onRefresh, onNavigate, customerId }: { preview: ResponseRecommendationPreview | null; loading: boolean; onRefresh: () => void; onNavigate?: (tabId: string, customerId?: string) => void; customerId?: string }) {
  if (!preview && loading) return <EmptyState title="Loading response preview" description="Generating read-only containment, investigation and notification recommendations." />;
  if (!preview) return <EmptyState title="No response preview loaded" description="Refresh to generate recommended response actions, ResponseOps template links and analyst/customer notes." />;
  const groups = Array.isArray(preview.responseActions) ? preview.responseActions : [];
  const templates = Array.isArray(preview.responseOpsTemplates) ? preview.responseOpsTemplates : [];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <MessageSquare className="mt-1 h-6 w-6 text-cyan-200" />
            <div>
              <h2 className="text-xl font-bold text-white">Response Recommendation Preview</h2>
              <p className="mt-2 max-w-4xl text-sm text-slate-300">Read-only analyst guidance. This does not update Sentinel, trigger a playbook, isolate a device, disable a user, or notify a customer automatically.</p>
            </div>
          </div>
          <button onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh preview
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {(preview.readOnlyGuarantees ?? []).map((item) => badge(item, 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100'))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          {groups.map((group, idx) => (
            <div key={`${group.category}-${idx}`} className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="text-lg font-semibold text-white">{group.category ?? 'Response actions'}</div>
                {badge(String(group.riskLevel ?? 'Medium'), String(group.riskLevel ?? '').toLowerCase() === 'high' ? 'border-red-400/40 bg-red-400/10 text-red-100' : 'border-slate-600 bg-slate-800 text-slate-200')}
              </div>
              <div className="mt-4 space-y-3">
                {(group.actions ?? []).map((action, actionIdx) => (
                  <div key={String(action.id ?? actionIdx)} className="rounded-xl border border-slate-700 bg-slate-900/75 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-semibold text-white">{String(action.title ?? 'Recommended action')}</div>
                      {badge(String(action.priority ?? 'Medium'), 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100')}
                    </div>
                    <div className="mt-2 text-sm leading-relaxed text-slate-300">{String(action.rationale ?? '')}</div>
                    <div className="mt-2 text-xs text-slate-500">Read-only preview: {action.readOnly === false ? 'no' : 'yes'}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-white">ResponseOps template links</div>
              <button onClick={() => onNavigate?.('responseops', customerId)} className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-slate-800">Open ResponseOps</button>
            </div>
            <div className="mt-4 space-y-3">
              {templates.length ? templates.map((template, idx) => (
                <div key={String(template.id ?? idx)} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
                  <div className="font-semibold text-white">{String(template.title ?? 'ResponseOps template')}</div>
                  <div className="mt-1 text-xs text-slate-400">{String(template.category ?? 'template')} · Risk {String(template.riskLevel ?? 'Medium')}</div>
                  <div className="mt-2 text-sm text-slate-300">{String(template.description ?? template.helper ?? '')}</div>
                </div>
              )) : <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm text-slate-400">No matching ResponseOps templates found. Sync the ResponseOps catalog to improve links.</div>}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-white">Customer update draft</div>
              <button onClick={() => downloadTextFile('simulation-lab-customer-update-draft.txt', preview.customerUpdateDraft || '', 'text/plain;charset=utf-8')} className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-slate-800">Download</button>
            </div>
            <pre className="mt-4 max-h-72 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-black/30 p-4 text-sm leading-relaxed text-slate-300">{preview.customerUpdateDraft || 'No customer update draft generated.'}</pre>
          </div>
          <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-white">Internal analyst notes</div>
              <button onClick={() => downloadTextFile('simulation-lab-internal-analyst-notes.md', preview.internalAnalystNotes || '', 'text/markdown;charset=utf-8')} className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-slate-800">Download</button>
            </div>
            <pre className="mt-4 max-h-96 max-w-full overflow-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-black/30 p-4 text-xs leading-relaxed text-slate-300">{preview.internalAnalystNotes || 'No internal analyst notes generated.'}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function CaseEvidencePackPanel({ links, result, loading, attaching, canExport, customerVisible, setCustomerVisible, onRefresh, onAttach, onNavigate, customerId }: { links: CaseEvidenceLink[]; result: CaseEvidenceAttachResult | null; loading: boolean; attaching: boolean; canExport: boolean; customerVisible: boolean; setCustomerVisible: (value: boolean) => void; onRefresh: () => void; onAttach: () => void; onNavigate?: (tabId: string, customerId?: string) => void; customerId?: string }) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <FileArchive className="mt-1 h-6 w-6 text-emerald-200" />
            <div>
              <h2 className="text-xl font-bold text-white">Case Evidence / Report Pack Integration</h2>
              <p className="mt-2 max-w-4xl text-sm text-slate-300">Attach Simulation Lab outputs as case evidence and create a ZIP export pack with replay snapshot, executive summary, customer-safe report, training pack and response recommendations.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={onRefresh} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh links
            </button>
            <button onClick={() => onNavigate?.('case_evidence', customerId)} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">
              <FileText className="h-4 w-4" /> Open Case Evidence
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-cyan-400/30 bg-cyan-500/10 p-4 text-sm text-cyan-50/90">
          Every generated attachment is audited. The export pack is stored in Case Evidence storage and does not modify Sentinel incidents, watchlists, bookmarks or playbooks.
        </div>
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-slate-700 bg-slate-900/75 p-4 md:flex-row md:items-center md:justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={customerVisible} onChange={(event) => setCustomerVisible(event.target.checked)} className="h-4 w-4 rounded border-slate-600 bg-slate-900" />
            Mark customer-safe report and pack as customer-visible
          </label>
          <button onClick={onAttach} disabled={!canExport || attaching} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
            {attaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Attach to Case Evidence
          </button>
        </div>
      </div>

      {result && (
        <div className="grid gap-3 md:grid-cols-3">
          <ComparisonCard label="Artifacts attached" value={String(result.attachments?.length ?? 0)} helper="Evidence attachments created" />
          <ComparisonCard label="Export pack" value={result.exportPack ? 'created' : 'not created'} helper={String(result.exportPack?.filename ?? 'No pack yet')} />
          <ComparisonCard label="Response preview" value={result.responsePreviewAvailable ? 'included' : 'not included'} helper="Response recommendations artifact" />
        </div>
      )}

      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
        <div className="text-lg font-semibold text-white">Linked Case Evidence artifacts</div>
        <div className="mt-4 space-y-3">
          {links.length ? links.map((link, idx) => (
            <div key={String(link.id ?? idx)} className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="font-semibold text-white">{link.filename || link.artifactType || 'Case Evidence artifact'}</div>
                  <div className="mt-1 text-xs text-slate-400">{link.artifactType} · {formatDate(link.createdAt)} · {link.customerVisible ? 'customer visible' : 'internal'}</div>
                </div>
                {badge(link.exportPackId ? 'export pack' : 'attachment', link.exportPackId ? 'border-violet-400/40 bg-violet-400/10 text-violet-100' : 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100')}
              </div>
              <div className="mt-2 text-xs text-slate-500">Attachment: {link.attachmentId || 'n/a'} · Export pack: {link.exportPackId || 'n/a'}</div>
            </div>
          )) : <EmptyState title="No Case Evidence links yet" description="Attach this simulation to Case Evidence to create audited artifacts and a report pack." />}
        </div>
      </div>
    </div>
  );
}


function ExportButton({ label, helper, format, exportingFormat, canExport, onClick }: { label: string; helper: string; format: 'json' | 'pdf_payload' | 'training_pack' | 'customer_safe_report'; exportingFormat: string | null; canExport: boolean; onClick: (format: 'json' | 'pdf_payload' | 'training_pack' | 'customer_safe_report', regenerateAi?: boolean) => Promise<void> }) {
  const busy = exportingFormat === format;
  return (
    <button onClick={() => void onClick(format, true)} disabled={!canExport || !!exportingFormat} className="rounded-2xl border border-slate-700 bg-slate-900/75 p-4 text-left transition hover:border-cyan-400/60 hover:bg-slate-900 disabled:opacity-60">
      <div className="flex items-center gap-2 text-base font-semibold text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4 text-cyan-200" />} {label}</div>
      <div className="mt-2 text-xs text-slate-400">{helper}</div>
    </button>
  );
}

function ExportPreview({ payload }: { payload: SimulationExportPayload }) {
  const pack = payload.executivePack ?? {};
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
        <div className="text-lg font-semibold text-white">Executive summary</div>
        <div className="mt-1 text-xs text-slate-500">Model: {pack.model ?? 'n/a'} · AI used: {pack.usedAi ? 'yes' : 'fallback'} {pack.aiWarning ? `· ${pack.aiWarning}` : ''}</div>
        <div className="mt-4 whitespace-pre-wrap rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm leading-relaxed text-slate-300">{pack.executiveSummary || 'No executive summary generated.'}</div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <ListPanel title="Key findings" items={pack.keyFindings ?? []} />
          <ListPanel title="Recommended actions" items={pack.recommendedActions ?? []} />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5">
        <div className="text-lg font-semibold text-white">Analyst training summary</div>
        <div className="mt-4 max-h-[520px] overflow-auto whitespace-pre-wrap rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm leading-relaxed text-slate-300">{pack.analystTrainingSummary || 'No analyst training summary generated.'}</div>
      </div>
      <div className="rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5 xl:col-span-2">
        <div className="text-lg font-semibold text-white">Customer-safe report preview</div>
        <div className="mt-4 max-h-[520px] overflow-auto whitespace-pre-wrap rounded-xl border border-slate-700 bg-slate-900/75 p-4 text-sm leading-relaxed text-slate-300">{pack.customerSafeReport || payload.customerSafeReport?.markdown || 'No customer-safe report generated.'}</div>
      </div>
    </div>
  );
}

function ListPanel({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900/75 p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="mt-2 space-y-2 text-sm text-slate-300">
        {items.length ? items.map((item, idx) => <div key={`${title}-${idx}`}>• {item}</div>) : <div className="text-slate-500">No items generated.</div>}
      </div>
    </div>
  );
}

function Guardrail({ text: label, positive = false }: { text: string; positive?: boolean }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-slate-700 bg-slate-900/70 p-3">
      {positive ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-300" /> : <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-300" />}
      <span>{label}</span>
    </div>
  );
}
